import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { getProfil, ueberarbeiteAbsatz } from '../api';
import { anschreibenAbsenderZeilen, buildAnschreibenAbsender } from '../utils/anschreibenAbsender';
import {
  AN_START_AUSRICHTUNG,
  startLayoutAusRects,
  mergeLayout,
  resizeMitEinrasten,
  gleicherAbstand,
  tastenVersatz,
  gruppenHuelle,
  ragtHinaus,
  aufSeitenVerteilen,
  seiteNachVerschieben,
} from '../utils/anLayout';
import { brieftextAusRohtext, rohtextMitBrieftext } from '../utils/anBrieftext';
import { hsvNachHex, hexNachHsv } from '../utils/farbe';
import { PLATZHALTER } from '../utils/themeUtils';

// Findet die KI keinen Firmennamen, steht ein Platzhalter wie <UNKNOWN> in den Daten.
// Der gehört nicht in einen Brief — die rote Meldung daneben sagt bereits alles.
// Klarnamen der festen Blöcke — nur für die Spur, die ein ausgeblendeter Block hinterlässt.
const AN_BLOCK_NAME = {
  absender: 'Absender',
  empfaenger: 'Empfänger',
  datum: 'Datum',
  betreff: 'Betreff',
  text: 'Brieftext',
};

// Ersatzmaße für die Spur eines ausgeblendeten Blocks, falls seine wirkliche Höhe nicht
// bekannt ist — etwa bei einem Brief, der ausgeblendet wurde, bevor es diese Messung gab.
// `breite` ist ein Anteil der Textbreite und greift nur, wenn auch keine Breite
// gespeichert ist. Die Zahlen bilden ab, wie ein Anschreiben nach DIN aussieht: Absender
// und Empfänger als kleine Blöcke oben, das Datum eine Zeile, der Betreff eine breite
// Zeile, der Brieftext die große Fläche darunter.
const AN_SPUR_ERSATZ = {
  absender:   { breite: 0.34, hoehe: 84 },
  empfaenger: { breite: 0.42, hoehe: 84 },
  datum:      { breite: 0.30, hoehe: 22 },
  betreff:    { breite: 0.62, hoehe: 24 },
  text:       { breite: 1,    hoehe: 430 },
};

const echterFirmenname = (stelle) => {
  const wert = (stelle?.unternehmen || stelle?.name || '').trim();
  return PLATZHALTER.includes(wert) ? '' : wert;
};

const UndoIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.92 8.97957H8.77L9.1 8.64957C9.39 8.35957 9.39 7.87957 9.1 7.58957C8.81 7.29957 8.33 7.29957 8.04 7.58957L6.47 9.15957C6.18 9.44957 6.18 9.92957 6.47 10.2196L8.04 11.7896C8.19 11.9396 8.38 12.0096 8.57 12.0096C8.76 12.0096 8.95 11.9396 9.1 11.7896C9.39 11.4996 9.39 11.0196 9.1 10.7296L8.84 10.4696H13.92C15.2 10.4696 16.25 11.5096 16.25 12.7996C16.25 14.0896 15.21 15.1296 13.92 15.1296H9C8.59 15.1296 8.25 15.4696 8.25 15.8796C8.25 16.2896 8.59 16.6296 9 16.6296H13.92C16.03 16.6296 17.75 14.9096 17.75 12.7996C17.75 10.6896 16.03 8.97957 13.92 8.97957Z"/>
  </svg>
);

const RedoIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.53 9.15957L15.96 7.58957C15.67 7.29957 15.19 7.29957 14.9 7.58957C14.61 7.87957 14.61 8.35957 14.9 8.64957L15.23 8.97957H10.08C7.97 8.97957 6.25 10.6996 6.25 12.8096C6.25 14.9196 7.97 16.6396 10.08 16.6396H15C15.41 16.6396 15.75 16.2996 15.75 15.8896C15.75 15.4796 15.41 15.1396 15 15.1396H10.08C8.8 15.1396 7.75 14.0996 7.75 12.8096C7.75 11.5196 8.79 10.4796 10.08 10.4796H15.16L14.9 10.7396C14.61 11.0296 14.61 11.5096 14.9 11.7996C15.05 11.9496 15.24 12.0196 15.43 12.0196C15.62 12.0196 15.81 11.9496 15.96 11.7996L17.53 10.2296C17.82 9.92957 17.82 9.45957 17.53 9.15957Z"/>
  </svg>
);

const CloseIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GripIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <circle cx="5" cy="3.5" r="1.4"/><circle cx="11" cy="3.5" r="1.4"/>
    <circle cx="5" cy="8" r="1.4"/><circle cx="11" cy="8" r="1.4"/>
    <circle cx="5" cy="12.5" r="1.4"/><circle cx="11" cy="12.5" r="1.4"/>
  </svg>
);

const EditPencilIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.26 3.60014L5.04997 12.2901C4.73997 12.6201 4.43997 13.2701 4.37997 13.7201L4.00997 16.9601C3.87997 18.1301 4.71997 18.9301 5.87997 18.7301L9.09997 18.1801C9.54997 18.1001 10.18 17.7701 10.49 17.4301L18.7 8.74014C20.12 7.24014 20.76 5.53014 18.55 3.44014C16.35 1.37014 14.68 2.10014 13.26 3.60014Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.89 5.05005C12.32 7.81005 14.56 9.92005 17.34 10.2" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 22H21" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SparkleIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L13.4 8.8L20 10L13.4 11.2L12 18L10.6 11.2L4 10L10.6 8.8L12 2Z"/>
    <path d="M5 17.5L5.9 20.1L8.5 21L5.9 21.9L5 24.5L4.1 21.9L1.5 21L4.1 20.1L5 17.5Z" opacity="0.6"/>
    <path d="M19 2.5L19.9 5.1L22.5 6L19.9 6.9L19 9.5L18.1 6.9L15.5 6L18.1 5.1L19 2.5Z" opacity="0.6"/>
  </svg>
);

const AlignLeftIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 3h12M1 6h8M1 9h12M1 12h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const AlignCenterIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 3h12M3 6h8M1 9h12M4 12h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const AlignRightIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 3h12M5 6h8M1 9h12M7 12h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const AlignJustifyIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 3h12M1 6h12M1 9h12M1 12h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

// ── Parser ────────────────────────────────────────────────────

export function parseAnschreiben(rohtext) {
  const zeilen = rohtext
    .split('\n')
    .map((z) => z.trim())
    .filter((z) => z && !z.startsWith('---'));

  let betreff = '';
  let betreffRohzeile = '';
  let anrede = '';
  const paragraphen = [];
  let grussGefunden = false;
  let gruss = '';
  let grussRohzeile = '';
  const unterschriftZeilen = [];

  for (const zeile of zeilen) {

    const zeileOhneSterne = zeile.replace(/\*\*/g, '').trim();
    if (!betreff && /^bewerbung\s+als/i.test(zeileOhneSterne)) {
      betreff = zeileOhneSterne;
      betreffRohzeile = zeile;
      continue;
    }

    if (/^sehr geehrte/i.test(zeileOhneSterne)) {
      anrede = zeileOhneSterne;
      continue;
    }

    if (zeile.startsWith('Mit freundlichen')) {
      grussGefunden = true;
      gruss = zeileOhneSterne;
      grussRohzeile = zeile;
      continue;
    }

    if (grussGefunden) {
      unterschriftZeilen.push({ text: zeileOhneSterne, rohzeile: zeile });
      continue;
    }

    paragraphen.push({ text: zeile.replace(/\*\*/g, '').trim(), rohzeile: zeile });
  }

  const anredeAnzeige = anrede || 'Sehr geehrte Damen und Herren,';
  return { betreff, betreffRohzeile, anrede: anredeAnzeige, paragraphen, gruss, grussRohzeile, unterschriftZeilen };
}

// ── Anschreiben Empfänger Panel ───────────────────────────────

export const AnschreibenDatumPanel = forwardRef(function AnschreibenDatumPanel(
  { stelle, profil, onSave, onClose, onClosingStart }, ref
) {
  const defaultIso = stelle?.brief_datum
    || (stelle?.generiert_am ? stelle.generiert_am.slice(0, 10) : new Date().toISOString().slice(0, 10));
  const defaultOrt = stelle?.brief_ort
    || profil?.ort
    || profil?.plz_ort?.split(' ').slice(1).join(' ')
    || '';
  const [datum, setDatum] = useState(defaultIso);
  const [ort, setOrt] = useState(defaultOrt);
  const [closing, setClosing] = useState(false);

  const handleClose = () => { onClosingStart?.(); setClosing(true); };
  useImperativeHandle(ref, () => ({ requestClose: handleClose }));

  const handleSave = () => { onSave({ brief_datum: datum, brief_ort: ort }); handleClose(); };

  return (
    <div
      className={`ssp-panel${closing ? ' is-closing' : ''}`}
      onAnimationEnd={() => { if (closing) onClose(); }}
    >
      <div className="ssp-header">
        <div className="ssp-header-inner">
          <span className="ssp-header-label">Datum bearbeiten</span>
        </div>
        <button className="ssp-close" onClick={handleClose} title="Schließen">
          <CloseIcon size={16} />
        </button>
      </div>
      <div className="ssp-body">
        <div className="ssp-field">
          <label className="ssp-label">Stadt</label>
          <input
            className="ssp-input"
            value={ort}
            onChange={e => setOrt(e.target.value)}
            placeholder="Hamburg"
          />
        </div>
        <div className="ssp-field">
          <label className="ssp-label">Datum</label>
          <input
            type="date"
            className="ssp-input"
            value={datum}
            onChange={e => setDatum(e.target.value)}
          />
        </div>
      </div>
      <div className="ssp-footer">
        <button className="ssp-btn-save" onClick={handleSave}>Speichern</button>
        <button className="ssp-btn-cancel" onClick={handleClose}>Abbrechen</button>
      </div>
    </div>
  );
});

export const AnschreibenAbsenderPanel = forwardRef(function AnschreibenAbsenderPanel(
  { stelle, profil, onSave, onClose, onClosingStart }, ref
) {
  const initial = buildAnschreibenAbsender(stelle, profil);
  const [felder, setFelder] = useState({
    absender_name: initial.name,
    absender_anschrift: initial.anschrift,
    absender_telefon: initial.telefon,
    absender_email: initial.email,
  });
  const [closing, setClosing] = useState(false);

  const handleClose = () => { onClosingStart?.(); setClosing(true); };
  useImperativeHandle(ref, () => ({ requestClose: handleClose }));

  const set = (key) => (e) => setFelder((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = () => { onSave(felder); handleClose(); };

  return (
    <div
      className={`ssp-panel${closing ? ' is-closing' : ''}`}
      onAnimationEnd={() => { if (closing) onClose(); }}
    >
      <div className="ssp-header">
        <div className="ssp-header-inner">
          <span className="ssp-header-label">Absender bearbeiten</span>
        </div>
        <button className="ssp-close" onClick={handleClose} title="Schließen">
          <CloseIcon size={16} />
        </button>
      </div>

      <div className="ssp-body">
        <div className="ssp-field">
          <label className="ssp-label">Name Nachname</label>
          <input
            className="ssp-input"
            value={felder.absender_name}
            onChange={set('absender_name')}
            placeholder="Max Mustermann"
          />
        </div>
        <div className="ssp-field">
          <label className="ssp-label">Anschrift</label>
          <textarea
            className="ssp-input ssp-textarea an-absender-textarea"
            value={felder.absender_anschrift}
            onChange={set('absender_anschrift')}
            placeholder={'Musterstraße 42\n80331 München'}
            spellCheck={false}
          />
        </div>
        <div className="ssp-field">
          <label className="ssp-label">Telefonnummer</label>
          <input
            className="ssp-input"
            value={felder.absender_telefon}
            onChange={set('absender_telefon')}
            placeholder="+49 170 1234567"
          />
        </div>
        <div className="ssp-field">
          <label className="ssp-label">E-Mail (optional)</label>
          <input
            className="ssp-input"
            value={felder.absender_email}
            onChange={set('absender_email')}
            placeholder="name@example.com"
          />
        </div>
      </div>

      <div className="ssp-footer">
        <button className="ssp-btn-save" onClick={handleSave}>Speichern</button>
        <button className="ssp-btn-cancel" onClick={handleClose}>Abbrechen</button>
      </div>
    </div>
  );
});

export const AnschreibenEmpfaengerPanel = forwardRef(function AnschreibenEmpfaengerPanel(
  { stelle, onSave, onClose, onClosingStart }, ref
) {
  const [felder, setFelder] = useState({
    empfaenger_ansprechperson: stelle?.empfaenger_ansprechperson || '',
    unternehmen: stelle?.unternehmen || stelle?.name || '',
    empfaenger_strasse: stelle?.empfaenger_strasse || '',
    empfaenger_plz_ort: stelle?.empfaenger_plz_ort || '',
    job_id: stelle?.job_id || '',
  });
  const [closing, setClosing] = useState(false);

  const handleClose = () => { onClosingStart?.(); setClosing(true); };
  useImperativeHandle(ref, () => ({ requestClose: handleClose }));

  const set = (key) => (e) => setFelder((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = () => { onSave(felder); handleClose(); };

  return (
    <div
      className={`ssp-panel${closing ? ' is-closing' : ''}`}
      onAnimationEnd={() => { if (closing) onClose(); }}
    >
      <div className="ssp-header">
        <div className="ssp-header-inner">
          <span className="ssp-header-label">Empfänger bearbeiten</span>
        </div>
        <button className="ssp-close" onClick={handleClose} title="Schließen">
          <CloseIcon size={16} />
        </button>
      </div>

      <div className="ssp-body">
        <div className="ssp-field">
          <label className="ssp-label">Firma / Unternehmen</label>
          <input
            className="ssp-input"
            value={felder.unternehmen}
            onChange={set('unternehmen')}
            placeholder="Musterfirma GmbH"
          />
        </div>
        <div className="ssp-field">
          <label className="ssp-label">Ansprechperson (optional)</label>
          <input
            className="ssp-input"
            value={felder.empfaenger_ansprechperson}
            onChange={set('empfaenger_ansprechperson')}
            placeholder="Frau Müller"
          />
        </div>
        <div className="ssp-field">
          <label className="ssp-label">Straße & Hausnummer</label>
          <input
            className="ssp-input"
            value={felder.empfaenger_strasse}
            onChange={set('empfaenger_strasse')}
            placeholder="Musterstraße 42"
          />
        </div>
        <div className="ssp-field">
          <label className="ssp-label">PLZ & Ort</label>
          <input
            className="ssp-input"
            value={felder.empfaenger_plz_ort}
            onChange={set('empfaenger_plz_ort')}
            placeholder="80331 München"
          />
        </div>
        <div className="ssp-field">
          <label className="ssp-label">Job-ID / Referenz (optional)</label>
          <input
            className="ssp-input"
            value={felder.job_id}
            onChange={set('job_id')}
            placeholder="REF-12345"
          />
        </div>
      </div>

      <div className="ssp-footer">
        <button className="ssp-btn-save" onClick={handleSave}>Speichern</button>
        <button className="ssp-btn-cancel" onClick={handleClose}>Abbrechen</button>
      </div>
    </div>
  );
});

// ── Anschreiben Absatz Panel ──────────────────────────────────

export const AnschreibenAbsatzPanel = forwardRef(function AnschreibenAbsatzPanel(
  { item, anRohtext, stelleId, profil, onSave, onClose, onClosingStart, inline }, ref
) {
  const [text, setText] = useState(item.text);
  const [kiPrompt, setKiPrompt] = useState('');
  const [kiLaden, setKiLaden] = useState(false);
  const [kiFehler, setKiFehler] = useState(null);
  const [closing, setClosing] = useState(false);

  const handleClose = () => {
    if (inline) return;
    onClosingStart?.();
    setClosing(true);
  };

  useImperativeHandle(ref, () => ({ requestClose: handleClose }));

  const handleSave = () => {
    onSave(item.rohzeile, text);
    if (!inline) handleClose();
  };

  const handleKiSend = async () => {
    if (!kiPrompt.trim()) return;
    setKiLaden(true);
    setKiFehler(null);
    try {
      const res = await ueberarbeiteAbsatz({
        absatz: text,
        anschreiben_kontext: anRohtext,
        nutzer_prompt: kiPrompt,
        stelle_id: stelleId,
        profil_snapshot: profil ? JSON.stringify(profil) : null,
      });
      setText(res.data.neuer_absatz);
      setKiPrompt('');
    } catch {
      setKiFehler('Fehler beim Überarbeiten. Bitte erneut versuchen.');
    } finally {
      setKiLaden(false);
    }
  };

  const onKiKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleKiSend();
    if (e.key === 'Escape') handleClose();
  };

  return (
    <div
      className={`ssp-panel${closing && !inline ? ' is-closing' : ''}${inline ? ' ssp-panel--inline' : ''}`}
      onAnimationEnd={() => { if (closing && !inline) onClose(); }}
    >
      <div className="ssp-header">
        <div className="ssp-header-inner">
          <span className="ssp-header-label">Absatz bearbeiten</span>
          <span className="asp-header-sublabel">{item.label}</span>
        </div>
        {!inline && (
          <button className="ssp-close" onClick={handleClose} title="Schließen">
            <CloseIcon size={16} />
          </button>
        )}
      </div>

      <div className="ssp-body">
        <div className="ssp-field">
          <label className="ssp-label">Text</label>
          <textarea
            className="ssp-input ssp-textarea asp-text-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
          />
        </div>

        <div className="asp-ki-divider">
          <span>KI-Überarbeitung</span>
        </div>

        <div className="ssp-field">
          <label className="ssp-label">Anweisung für KI</label>
          <textarea
            className="ssp-input ssp-textarea asp-ki-textarea"
            value={kiPrompt}
            onChange={(e) => setKiPrompt(e.target.value)}
            onKeyDown={onKiKeyDown}
            placeholder="z.B. Kürzer formulieren, sachlicher…"
          />
          {kiFehler && <div className="asp-ki-fehler">{kiFehler}</div>}
          <button
            className="asp-ki-send-btn"
            onClick={handleKiSend}
            disabled={kiLaden || !kiPrompt.trim()}
          >
            <SparkleIcon size={14} />
            {kiLaden ? 'Überarbeitung läuft…' : 'KI Überarbeiten'}
          </button>
        </div>
      </div>

      <div className="ssp-footer">
        <button className="ssp-btn-save" onClick={handleSave}>Speichern</button>
        {!inline && <button className="ssp-btn-cancel" onClick={handleClose}>Abbrechen</button>}
      </div>
    </div>
  );
});

// ── Inline-Edit Helper ────────────────────────────────────────

/**
 * Darf aus diesem Feld gespeichert werden?
 *
 * Der Brief wird zweimal gezeichnet: sichtbar auf dem Blatt und unsichtbar daneben als
 * Messkopie, aus der die Startpositionen abgelesen werden. Der Inhalt eines Feldes wird
 * über `innerText` gelesen, und das liefert den GERENDERTEN Text — in einem Bereich mit
 * `visibility: hidden` ist er immer leer.
 *
 * Am 2026-08-09 hat genau das den Brief eines Nutzers gelöscht: Beim Verlassen des
 * Bearbeitungsmodus wurden alle Felder zum Speichern aufgefordert, die unsichtbare
 * Messkopie antwortete mit einer leeren Zeichenkette, und die überschrieb den Rohtext.
 *
 * Die Regel gilt für jedes Feld, nicht nur für das eine, in dem es aufgefallen ist:
 * Aus etwas Unsichtbarem wird nie gespeichert.
 */
// Die Optionen sind nicht optional: Ohne sie beachtet `checkVisibility` die CSS-Eigenschaft
// `visibility` gerade NICHT — und genau die versteckt den Messklon (`.cv-measure-hidden`).
// Die Prüfung hätte den Fall, für den sie geschrieben wurde, sonst durchgewinkt.
const unsichtbarNichtSpeichern = (el) =>
  !el || (typeof el.checkVisibility === 'function' && !el.checkVisibility({
    visibilityProperty: true,
    opacityProperty: true,
    contentVisibilityAuto: true,
  }));

function InlineText({ value, rohzeile, className, onChange, multiLine = true, htmlValue, itemKey }) {
  const ref = useRef(null);
  const getHtml = () => {
    const h = (htmlValue && htmlValue.text === value) ? htmlValue.html : escHtml(value);
    return h.replace(/\n/g, '<br>');
  };

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = getHtml();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.innerHTML = getHtml();
    }
  }, [value, htmlValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    // Siehe `unsichtbarNichtSpeichern`: Aus einem unsichtbaren Feld gelesener Text ist
    // immer leer und würde den echten überschreiben.
    if (unsichtbarNichtSpeichern(ref.current)) return;
    const plain = (ref.current?.innerText ?? '').replace(/\n$/, '');
    const newlines = s => (s.match(/\n/g) || []).length;
    if (plain !== value && newlines(plain) < newlines(value) && plain.replace(/\s+/g, '') === value.replace(/\s+/g, '')) return;
    const sanitized = sanitizeHtml(ref.current?.innerHTML ?? '');
    const hasFormatting = /<(?:b|strong|i|em|u)\b/i.test(sanitized) || /<span[^>]+style=/i.test(sanitized);
    const htmlPayload = itemKey ? { itemKey, html: hasFormatting ? sanitized : null, text: plain } : null;
    if (plain !== value || (itemKey && hasFormatting !== !!(htmlValue?.text === value && htmlValue?.html))) {
      onChange(rohzeile, plain, htmlPayload);
    }
  };

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={`an-inline-editable${className ? ' ' + className : ''}`}
      onBlur={commit}
      onKeyDown={!multiLine ? (e => { if (e.key === 'Enter') { e.preventDefault(); ref.current?.blur(); } }) : undefined}
    />
  );
}

const escHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function sanitizeHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const allowed = new Set(['b','strong','i','em','u','br','span']);
  const blockTags = new Set(['div','p','li']);
  const walk = (node) => {
    const children = [...node.childNodes];
    for (const child of children) {
      if (child.nodeType === Node.TEXT_NODE) continue;
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase();
        if (!allowed.has(tag)) {
          // Block-Elemente repräsentieren Zeilenumbrüche — <br> einfügen
          if (blockTags.has(tag) && child.previousSibling) {
            node.insertBefore(document.createElement('br'), child);
          }
          while (child.firstChild) node.insertBefore(child.firstChild, child);
          node.removeChild(child);
        } else {
          [...child.attributes].forEach(a => {
            if (!(tag === 'span' && a.name === 'style')) child.removeAttribute(a.name);
          });
          walk(child);
        }
      }
    }
  };
  walk(tmp);
  return tmp.innerHTML;
}

function EditableText({ value, rohzeile, className, onChange, itemKey, htmlValue }) {
  const ref = useRef(null);
  const discarding = useRef(false);

  const getHtml = () => (htmlValue && htmlValue.text === value) ? htmlValue.html : escHtml(value);

  // Mount: set initial HTML
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = getHtml();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prop change: only update DOM when NOT focused (don't stomp in-progress edits or execCommand results)
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.innerHTML = getHtml();
    }
  }, [value, htmlValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    if (discarding.current) { discarding.current = false; return; }
    if (unsichtbarNichtSpeichern(ref.current)) return;
    const plain = (ref.current?.innerText ?? '').replace(/\n$/, '');
    const rawHtml = ref.current?.innerHTML ?? '';
    const sanitized = sanitizeHtml(rawHtml);
    const hasFormatting = /<(?:b|strong|i|em|u)\b/i.test(sanitized) || /<span[^>]+style=/i.test(sanitized);
    const htmlPayload = itemKey
      ? { itemKey, html: hasFormatting ? sanitized : null, text: plain }
      : null;
    if (plain !== value || (htmlPayload && hasFormatting !== !!(htmlValue?.text === value && htmlValue?.html))) {
      onChange(rohzeile, plain, htmlPayload);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      discarding.current = true;
      if (ref.current) ref.current.innerHTML = getHtml();
      ref.current?.blur();
    }
  };

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={`an-editable-text${className ? ' ' + className : ''}`}
      onBlur={commit}
      onKeyDown={handleKeyDown}
    />
  );
}

function BodyTextarea({ rawBody, onChange }) {
  const ref = useRef(null);
  const prevRaw = useRef(rawBody);
  const [text, setText] = useState(rawBody);
  const textRef = useRef(rawBody);
  const onChangeRef = useRef(onChange);

  if (prevRaw.current !== rawBody) {
    prevRaw.current = rawBody;
    setText(rawBody);
    textRef.current = rawBody;
  }

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    return () => {
      if (textRef.current !== prevRaw.current) {
        onChangeRef.current(prevRaw.current, textRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
  });

  return (
    <textarea
      ref={ref}
      className="an-body-textarea"
      value={text}
      onChange={e => { setText(e.target.value); textRef.current = e.target.value; }}
      onBlur={() => { if (text !== prevRaw.current) onChange(prevRaw.current, text); }}
    />
  );
}

// `itemKey` sagt, in welchem Fach des Layouts der Inhalt landet. Der Brieftext heißt
// seit jeher `text`; frei angelegte Textfelder bringen ihre eigene Kennung mit.
function BodyEditable({ rawBody, htmlBody, onChange, itemKey = 'text' }) {
  const ref = useRef(null);
  const discarding = useRef(false);
  const htmlOk = (h, body) => h && h.text === body && (!body.includes('\n') || h.html.includes('<br') || h.html.includes('\n'));
  const getHtml = () => {
    const h = htmlOk(htmlBody, rawBody) ? htmlBody.html : escHtml(rawBody);
    return h.replace(/\n/g, '<br>');
  };

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = getHtml();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.innerHTML = getHtml();
    }
  }, [rawBody, htmlBody]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitRef = useRef(null);
  const commit = () => {
    if (!ref.current) return;
    if (discarding.current) { discarding.current = false; return; }
    // Ohne Empfänger gibt es nichts zu schreiben. Das gilt für den unsichtbaren Messklon:
    // Er rendert denselben Brief, um die Startpositionen zu messen, darf aber niemals
    // etwas speichern.
    if (!onChange) return;
    if (unsichtbarNichtSpeichern(ref.current)) return;
    const plain = (ref.current?.innerText ?? '').replace(/\n$/, '');
    const sanitized = sanitizeHtml(ref.current?.innerHTML ?? '');
    const hasFormatting = /<(?:b|strong|i|em|u)\b/i.test(sanitized) || /<span[^>]+style=/i.test(sanitized);
    // Nur schreiben, wenn sich wirklich etwas geändert hat — wie in `EditableText` und
    // `InlineText`. Ohne diesen Vergleich erzeugte schon das bloße Hinein- und
    // Herausklicken einen Server-Schreibvorgang und einen leeren Rückgängig-Schritt;
    // der Zähler an den Pfeilen stieg, und ein Klick darauf schien nichts zu tun.
    const schonFormatiert = !!(htmlBody?.text === rawBody && htmlBody?.html);
    if (plain === rawBody && hasFormatting === schonFormatiert) return;
    onChange(rawBody, plain, { itemKey, html: hasFormatting ? sanitized : null, text: plain });
  };
  commitRef.current = commit;

  useEffect(() => {
    const handler = () => commitRef.current?.();
    document.addEventListener('cv-body-commit', handler);
    return () => document.removeEventListener('cv-body-commit', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className="an-body-editable"
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          // Abbruch heißt Abbruch: Ohne diese Marke liefe direkt danach `commit` über den
          // Blur und schriebe den wiederhergestellten Stand doch noch weg.
          discarding.current = true;
          if (ref.current) ref.current.innerHTML = getHtml();
          ref.current?.blur();
        }
      }}
    />
  );
}

// Blatt mit Pluszeichen — nach dem Sinnbild, das der User geschickt hat.
const SeiteNeuIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9" />
    <path d="M19 8V5a2 2 0 0 0-2-2" />
    <path d="M19 12v8M15 16h8" />
  </svg>
);

// Dasselbe Blatt, aber mit Minuszeichen — Gegenstück zu „Seite hinzufügen".
const SeiteWegIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9" />
    <path d="M19 8V5a2 2 0 0 0-2-2" />
    <path d="M15 16h8" />
  </svg>
);

// Schriftzeichen mit Pluszeichen — neues Textfeld.
const TextfeldNeuIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6V4h12v2M10 4v14M7 18h6" />
    <path d="M18 13v8M14 17h8" />
  </svg>
);

const SNAP_THRESHOLD = 12;

// Wie weit die Maus wandern muss, bis aus einem Klick ein Zug wird. In Bildschirmpunkten.
// Ohne diese Ruhezone verschiebt schon das leichte Zittern beim Klicken den Block.
const ZUG_TOTZONE = 4;

// Vier Ecken und die beiden Seitenmitten — wie bei Canva. Ober- und Untermitte gibt es
// dort nicht; sie sind auf Wunsch des Users am 2026-08-10 entfernt worden.
const RESIZE_HANDLES = ['tl','tr','ml','mr','bl','br'];

// Welche Ecke beim Ziehen stehen bleibt — der Anker liegt immer gegenüber dem Griff.
const ANKER_ORIGIN = {
  tl: 'right bottom',
  tr: 'left bottom',
  bl: 'right top',
  br: 'left top',
};

// Grenzen der Schriftgröße, wie in der Werkzeugleiste.
const MIN_FONT = 8, MAX_FONT = 36;

// Pfeiltasten verschieben den ausgewählten Block, mit Umschalt in Zehnerschritten.
const PFEIL_RICHTUNG = {
  ArrowLeft:  { x: -1, y: 0 },
  ArrowRight: { x:  1, y: 0 },
  ArrowUp:    { x: 0, y: -1 },
  ArrowDown:  { x: 0, y:  1 },
};

// Steht der Schreibcursor im Text, gehören die Tasten dem Text und nicht dem Block.
const tastenGehoerenDemText = () => {
  const el = document.activeElement;
  return !!el && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA');
};

function DragBlock({ blockKey, offsets, onOffsetChange, isSelected, onSelect, onSnapChange, onStylePatch, onKiEdit, blockStyles, auswahl, gruppenZug, onGruppenZug, onGruppeVerschieben, blatt, sicher, onRandVerletzt, seitenMass, letzteSeite, onZug, children }) {
  const [liveDelta, setLiveDelta] = useState({ x: 0, y: 0 });
  const [liveResize, setLiveResize] = useState(null);
  // Zwei Zustände wie bei Canva: gewählt (der ganze Block ist Ziehfläche) und im Text
  // (der Zeiger gehört dem Schreiben, verschoben wird gar nicht mehr). Der zweite Klick
  // auf einen schon gewählten Block führt hinein, das Abwählen wieder heraus.
  const [imText, setImText] = useState(false);
  const [geparkt, setGeparkt] = useState(false);
  const liveDeltaRef = useRef({ x: 0, y: 0 });
  const basePosRef = useRef(null);
  const selfRef = useRef(null);
  const snapLinieRef = useRef(null);
  const huelleRef = useRef(null);

  const saved = offsets?.[blockKey];
  const base = saved != null && typeof saved === 'object' ? saved : null;

  // Ohne onOffsetChange ist der Block reine Anzeige: gleiche Position, keine Bedienung.
  const interaktiv = !!onOffsetChange;

  // Sind mehrere Blöcke gewählt und dieser ist einer davon, bewegt er sich nur noch
  // im Verbund. `null` heißt: dieser Block ist für sich allein unterwegs.
  const gruppe = (auswahl?.length > 1 && auswahl.includes(blockKey)) ? auswahl : null;
  const imBund = !!gruppe;

  // Zwei Rahmen, zwei Stufen: Ragt der Block über den Sicherheitsrand, leuchtet der
  // Rand rot. Ragt er über die Blattkante, gilt er als abgelegt — dann wird er
  // ausgegraut und erscheint nicht im fertigen Dokument. Beides schon bei einem
  // Millimeter Überstand, und beides sofort beim Ziehen, nicht erst beim Loslassen.
  //
  // Das Rot bleibt auch dann an, wenn der Block ganz draußen liegt. Erst hatte ein
  // abgelegter Block die Warnung abgeschaltet, damit der Rand nicht dauerhaft leuchtet —
  // der User will genau das Gegenteil: Solange etwas übersteht, soll man es sehen.
  //
  // Das Ausgrauen läuft bewusst auch außerhalb des Bearbeitungsmodus: Das PDF entsteht
  // aus der reinen Anzeige, und dort muss die Markierung ebenfalls sitzen, sonst landete
  // ein abgelegter Block doch wieder im fertigen Dokument.
  // Gemeldet werden zwei verschiedene Dinge. `rand` färbt den Streifen rot und gilt für
  // alle vier Seiten. `unten` ist enger: Nur wenn ein Block unter der Blattkante endet,
  // passt sein Ende nicht mehr aufs Papier — und genau dafür braucht der User den
  // Hinweis samt Knopf für eine neue Seite. Ein seitlich abgelegter Block soll ihn
  // nicht auslösen.
  const pruefeLage = (rect) => {
    setGeparkt(!!rect && ragtHinaus(rect, blatt));
    // Ein Block ohne Inhalt kann nichts verlieren. Die Meldung sagt „gedruckt wird nur,
    // was auf dem Papier steht" — bei einem leeren Kasten steht nirgends etwas, weder
    // auf dem Papier noch daneben. Sie war dort also eine Warnung ohne Verlust, und
    // schlimmer: Ein leeres Feld unterhalb des Blattes ist auf dem Bildschirm nicht zu
    // sehen. Der User bekam eine Beschwerde über etwas, das er nicht finden konnte, und
    // hielt sie deshalb zu Recht für falsch (gemeldet am 2026-08-11).
    // Der rote Streifen bleibt davon unberührt: Er zeigt eine Lage, keinen Verlust.
    const hatInhalt = (selfRef.current?.textContent || '').trim().length > 0;
    onRandVerletzt?.(blockKey, {
      rand:  !!rect && ragtHinaus(rect, sicher),
      // Gemessen wird an der Blattkante, nicht am Sicherheitsrand: Die Meldung spricht
      // vom Drucken, und gedruckt wird alles, was auf dem Papier liegt.
      unten: hatInhalt && !!rect && !!sicher && rect.top + (rect.height ?? 0) > sicher.papierUnten,
    });
  };

  // Um wie viel ist das Blatt verkleinert? Wird es auf 90 % gezeigt, entspricht ein
  // Mausweg von neun Punkten auf dem Schirm zehn Punkten auf dem Papier. Alle
  // gespeicherten Koordinaten zählen in Papiermaßen, der Mausweg kommt in
  // Bildschirmpunkten — ohne diese Umrechnung wanderte jeder Block zu kurz.
  //
  // Der Faktor wird am Blatt selbst abgelesen und nicht aus dem Stylesheet abgeschrieben:
  // So kann er nicht auseinanderlaufen, egal welchen Wert dort jemand einträgt.
  const blattSkala = () => {
    const el = selfRef.current?.closest('.an-pagina');
    if (!el?.offsetWidth) return 1;
    const s = el.getBoundingClientRect().width / el.offsetWidth;
    return s > 0.01 ? s : 1;
  };

  // Die eigene Höhe steht in keiner gespeicherten Zahl, sie hängt am Text — also messen.
  const eigenesRect = (dx = 0, dy = 0) => (base ? {
    left:   base.left + dx,
    top:    base.top  + dy,
    width:  base.width ?? selfRef.current?.offsetWidth ?? 0,
    height: selfRef.current?.offsetHeight ?? 0,
  } : null);

  // `blatt` und `sicher` sind Zustand und behalten ihre Identität zwischen zwei
  // Neuzeichnungen. Das ist hier entscheidend: Früher wurde die Papierfläche bei jedem
  // Neuzeichnen neu gebaut, dieser Effekt lief deshalb ständig mit — und überschrieb
  // die laufende Meldung aus dem Ziehen sofort wieder mit der Ruhelage. Rot erschien
  // dadurch erst nach dem Loslassen.
  useLayoutEffect(() => {
    pruefeLage(base ? eigenesRect() : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, blatt, sicher]);

  // Wer den Block abwählt, verlässt auch den Text. Sonst bliebe ein Block schreibbereit,
  // den man gar nicht mehr sieht — und der nächste Klick landete wieder im Text statt
  // beim Block.
  useEffect(() => { if (!isSelected) setImText(false); }, [isSelected]);

  // Verschwindet der Block, verschwindet auch seine Warnung — sonst bliebe der Rand rot
  // und die Meldung „endet unterhalb des Blattes" stünde da, obwohl der genannte Block
  // längst gelöscht ist.
  //
  // Genau das passierte vorher: Der Aufräum-Effekt hatte eine leere Abhängigkeitsliste und
  // hielt damit für immer die Meldefunktion aus dem ALLERERSTEN Durchlauf fest. Wer den
  // Bearbeitungsmodus erst nach dem Laden einschaltet, hat dort aber noch gar keine —
  // die Anmeldung der Warnung lief über die aktuelle Funktion, die Abmeldung ins Leere.
  // Ergebnis: eine Warnung, die sich auf nichts mehr bezieht und nicht mehr wegzubekommen
  // ist. Der Umweg über die Referenz hält die Funktion aktuell, ohne den Effekt bei jeder
  // Neuzeichnung neu aufzusetzen. Gemeldet vom User am 2026-08-11.
  const randMeldungRef = useRef(onRandVerletzt);
  randMeldungRef.current = onRandVerletzt;
  useEffect(() => () => randMeldungRef.current?.(blockKey, { rand: false, unten: false }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // Die Höhen stehen nicht in den Positionen — sie hängen am Text und werden gemessen.
  const huelleFuer = (keys) => {
    const container = selfRef.current?.closest('.anschreiben');
    const hoehen = {};
    for (const k of keys) {
      const el = container?.querySelector(`[data-block-key="${k}"]`);
      if (el) hoehen[k] = el.offsetHeight;
    }
    return gruppenHuelle(keys, offsets || {}, hoehen);
  };

  // Die Rechtecke der übrigen Blöcke. `eigene` bleibt außen vor — sonst rastete ein
  // Block an sich selbst ein und ließe sich nicht mehr bewegen.
  //
  // Breite und Höhe werden beide gemessen und nicht aus den gespeicherten Zahlen
  // genommen: Eine Breite steht dort nur, wenn der Block schon einmal in der Größe
  // geändert wurde. Früher wurde in diesem Fall ersatzweise die Breite des gezogenen
  // Blocks eingesetzt — dadurch entstanden Hilfslinien an Stellen, an denen in
  // Wahrheit gar keine Kante liegt. Findet sich kein Element, gibt es auch keine Linie.
  const andereRects = (container, eigene) =>
    Object.entries(offsets || {})
      .filter(([k, v]) => !eigene.includes(k) && v && typeof v === 'object' && v.left != null && v.top != null)
      .map(([k, v]) => {
        const el = container.querySelector(`[data-block-key="${k}"]`);
        return el ? { left: v.left, top: v.top, width: el.offsetWidth, height: el.offsetHeight } : null;
      })
      .filter(Boolean);

  // Alle Linien, an denen etwas einrasten kann: der Textbereich mit Kanten und Mitte,
  // die Kanten und Mitten der übrigen Blöcke — und der Sicherheitsrand selbst, die
  // äußerste gestrichelte Linie. Waagerecht zählt neben linker Kante und Mitte auch
  // die rechte Kante: bei rechtsbündigen Blöcken wie Absender und Datum ist genau sie
  // die aussagekräftige Linie.
  const snapPunkte = (rects, cw, ch) => ({
    x: [
      0, cw / 2, cw,
      ...(sicher ? [sicher.links, sicher.rechts] : []),
      ...rects.flatMap(r => [r.left, r.left + r.width / 2, r.left + r.width]),
    ],
    y: [
      0, ch / 2, ch,
      ...(sicher ? [sicher.oben, sicher.unten] : []),
      ...rects.flatMap(r => [r.top, r.top + r.height / 2, r.top + r.height]),
    ],
  });

  // Ohne Hülle rastet der Block selbst ein, mit Hülle das Rechteck um die ganze Gruppe.
  const applySnap = (rawDelta, huelle) => {
    const container = selfRef.current?.closest('.anschreiben');
    if (!container || !basePosRef.current) return { delta: rawDelta, lines: { x: null, y: null } };
    const cw = container.offsetWidth;
    const bw = huelle?.width  ?? selfRef.current.offsetWidth;
    const bh = huelle?.height ?? selfRef.current.offsetHeight;
    const startLeft = huelle?.left ?? basePosRef.current.left;
    const startTop  = huelle?.top  ?? basePosRef.current.top;
    const rawLeft   = startLeft + rawDelta.x;
    const rawRight  = rawLeft + bw;
    const rawTop    = startTop  + rawDelta.y;
    const rawBottom = rawTop + bh;

    const ch = container.offsetHeight;
    const rects = andereRects(container, huelle?.keys ?? [blockKey]);
    const { x: snapXPoints, y: snapYPoints } = snapPunkte(rects, cw, ch);

    let snappedLeft = rawLeft, snapX = null, bestX = SNAP_THRESHOLD;
    for (const pt of snapXPoints) {
      const dL = Math.abs(rawLeft  - pt);
      const dR = Math.abs(rawRight - pt);
      if (dL < bestX) { snappedLeft = pt;      snapX = pt; bestX = dL; }
      if (dR < bestX) { snappedLeft = pt - bw; snapX = pt; bestX = dR; }
    }

    let snappedTop = rawTop, snapY = null, bestY = SNAP_THRESHOLD;
    for (const pt of snapYPoints) {
      const dT = Math.abs(rawTop    - pt);
      const dB = Math.abs(rawBottom - pt);
      if (dT < bestY) { snappedTop = pt;      snapY = pt; bestY = dT; }
      if (dB < bestY) { snappedTop = pt - bh; snapY = pt; bestY = dB; }
    }

    // Gleiche Abstände: Steht zwischen zwei Nachbarn Platz, rastet der Block auf die
    // Mitte ein — die Lücke nach oben ist dann so groß wie die nach unten. Das ist eine
    // eigene Art von Einrasten, deshalb tritt es gegen die Kantenlinien an und gewinnt
    // nur, wenn es näher liegt. Statt einer durchgehenden Linie werden die beiden
    // gleich großen Lücken selbst gezeigt.
    let gleich = null;
    const mitte = gleicherAbstand({ top: rawTop, height: bh }, rects);
    if (mitte && Math.abs(rawTop - mitte.top) < bestY) {
      snappedTop = mitte.top;
      snapY = null;
      gleich = { oben: mitte.oben, unten: mitte.unten, abstand: mitte.abstand };
    }

    return {
      delta: { x: snappedLeft - startLeft, y: snappedTop - startTop },
      lines: { x: snapX, y: snapY, gleich: gleich && { ...gleich, x: snappedLeft + bw / 2 } },
    };
  };

  // Pfeiltasten verschieben den Block und rasten dabei an denselben Hilfslinien ein
  // wie das Ziehen. Deshalb sitzt die Abfrage hier im Block und nicht in der
  // Hauptkomponente: nur hier liegt die Einrast-Rechnung.
  // Bei einer Gruppe hört nur der erste Block auf die Tasten und schiebt alle mit —
  // sonst würden fünf Blöcke denselben Tastendruck fünfmal ausführen.
  const hoertAufTasten = interaktiv && !!base && (isSelected || gruppe?.[0] === blockKey);
  useEffect(() => {
    if (!hoertAufTasten) return;
    const linieLoeschen = () => onSnapChange?.({ x: null, y: null });
    const onKey = (e) => {
      if (tastenGehoerenDemText() || e.metaKey || e.ctrlKey || e.altKey) return;
      const richtung = PFEIL_RICHTUNG[e.key];
      if (!richtung) return;
      e.preventDefault();

      // Ein Schritt muss auf dem Bildschirm mindestens einen ganzen Bildpunkt weit
      // reichen. Sonst passiert genau das, was der User am 2026-08-11 gemeldet hat:
      // „nicht der ganze Block bewegt sich, nur einzelne Zeilen, beim nächsten Druck
      // andere". Gemessen nachgestellt — das Blatt stand auf 46 %, ein Schritt von einem
      // Papierpunkt war also ein halber Bildpunkt. Der Block wandert dann rechnerisch
      // völlig richtig, alle Zeilen um denselben Betrag; nur kann der Bildschirm keine
      // halben Punkte zeichnen. Jede Zeile rundet für sich, mal auf, mal ab — und weil
      // jede Zeile an einer anderen Nachkommastelle sitzt, erwischt es bei jedem Druck
      // andere Zeilen. Der Block sieht dabei aus, als zerfiele er.
      //
      // Also wird der Schritt am Anzeigemaßstab bemessen statt am Papier. Bei 100 %
      // bleibt es beim einen Punkt wie bisher, klein gezoomt werden es zwei oder drei.
      const sichtbarerPunkt = Math.max(1, Math.round(1 / (blattSkala() * (window.devicePixelRatio || 1))));
      const schritt = sichtbarerPunkt * (e.shiftKey ? 10 : 1);
      const roh = { x: richtung.x * schritt, y: richtung.y * schritt };
      basePosRef.current = base;
      const { delta, lines } = applySnap(roh, gruppe ? huelleFuer(gruppe) : undefined);

      const genutzt = tastenVersatz(roh, delta);

      const eingerastet = genutzt.x !== roh.x || genutzt.y !== roh.y;
      // Gezeigt wird nur die Linie der Achse, auf der wirklich eingerastet wurde.
      onSnapChange?.(eingerastet
        ? {
            x: genutzt.x !== roh.x ? lines.x : null,
            y: genutzt.y !== roh.y ? lines.y : null,
            gleich: genutzt.y !== roh.y ? lines.gleich : null,
          }
        : { x: null, y: null });
      clearTimeout(snapLinieRef.current);
      if (eingerastet) snapLinieRef.current = setTimeout(linieLoeschen, 900);

      // Gedrückt gehaltene Pfeiltasten sind eine Bedienung, kein Dutzend: Die Kennung
      // führt oben dazu, dass daraus ein Rückgängig-Schritt wird. Sie nennt den Block
      // mit, damit die Folge endet, sobald ein anderer Block an der Reihe ist.
      const folge = `tasten:${gruppe ? gruppe.join(',') : blockKey}`;
      if (gruppe) onGruppeVerschieben?.(gruppe, genutzt, folge);
      else onOffsetChange(blockKey, { ...base, top: base.top + genutzt.y, left: base.left + genutzt.x }, undefined, folge);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(snapLinieRef.current);
      linieLoeschen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoertAufTasten, base, offsets, blockKey, auswahl]);

  // Auf welcher Seite liegt dieser Block, und wo käme er nach einem Zug an?
  // Weiter als bis zum letzten vorhandenen Blatt geht es nicht: Eine neue Seite legt
  // der User selbst an. Ein Block, der darüber hinaus gezogen wird, bleibt auf dem
  // letzten Blatt und steht dort unten über — sichtbar, mit Hinweis und Knopf darunter.
  const eigeneSeite = base?.seite ?? 0;
  const zielSeiteFuer = (dy) => {
    if (!seitenMass) return eigeneSeite;
    const { seite } = seiteNachVerschieben({ seite: eigeneSeite, top: basePosRef.current?.top ?? 0 }, dy, seitenMass);
    return Math.min(seite, letzteSeite ?? seite);
  };

  const startDrag = (e, opts = {}) => {
    e.preventDefault();
    if (!opts.skipSelect) { onSelect(blockKey, e.shiftKey); e.stopPropagation(); }
    basePosRef.current = base || { top: 0, left: 0 };
    huelleRef.current = gruppe ? huelleFuer(gruppe) : null;
    const sx = e.clientX, sy = e.clientY;
    // Die Totzone stand bisher auf null, solange niemand ausdrücklich eine mitgab — und
    // das tat keine der drei Aufrufstellen. Damit verschob schon ein einziger Punkt
    // Handbewegung den Block: Wer nur klicken wollte, verrückte ihn versehentlich.
    // Gemessen wird in Bildschirmpunkten, denn es geht um die Ruhe der Hand, nicht um
    // Papiermaße — sonst hinge die Totzone an der Größe der Anzeige.
    const threshold = opts.threshold ?? ZUG_TOTZONE;
    let thresholdMet = !threshold;
    // Einmal zu Beginn ablesen: Während eines Zuges ändert sich die Größe des Blattes
    // nicht, und ein Messen bei jeder Zeigerbewegung würde den Browser bremsen.
    const skala = blattSkala();
    // Zeigererfassung: der Block bleibt am Zeiger, auch wenn er das Fenster verlässt.
    if (e.pointerId != null) e.currentTarget.setPointerCapture?.(e.pointerId);
    const onMove = (mv) => {
      const rohX = mv.clientX - sx, rohY = mv.clientY - sy;
      if (!thresholdMet) {
        if (Math.abs(rohX) < threshold && Math.abs(rohY) < threshold) return;
        thresholdMet = true;
      }
      const dx = rohX / skala, dy = rohY / skala;
      const { delta, lines } = applySnap({ x: dx, y: dy }, huelleRef.current || undefined);
      liveDeltaRef.current = delta;
      // In der Gruppe zeichnet nicht jeder Block seinen eigenen Versatz, sondern alle
      // lesen denselben aus einer Stelle — sonst liefen sie auseinander.
      if (gruppe) onGruppenZug?.({ keys: gruppe, delta });
      else setLiveDelta({ ...delta });
      onSnapChange?.(lines);
      pruefeLage(eigenesRect(delta.x, delta.y));
      // Schon während des Zuges melden, wo der Block landen würde. Die Anzeige hebt die
      // Ausgangsseite über die folgenden Blätter — sonst verschwände der Block beim
      // Ziehen nach unten hinter dem nächsten weißen Blatt — und hebt die Zielseite hervor.
      onZug?.({ von: eigeneSeite, ziel: zielSeiteFuer(delta.y) });
    };
    const onUp = (mu) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      onSnapChange?.({ x: null, y: null });
      onZug?.(null);
      if (gruppe) onGruppenZug?.(null);
      if (thresholdMet) {
        const d = liveDeltaRef.current;
        if (Math.abs(d.x) > 2 || Math.abs(d.y) > 2) {
          const bp = basePosRef.current;
          if (gruppe) onGruppeVerschieben?.(gruppe, d);
          else {
            // Beim Loslassen entscheidet sich, auf welchem Blatt der Block liegt. `top`
            // zählt danach von der Oberkante der neuen Seite — sonst säße er dort um eine
            // ganze Seitenhöhe zu tief.
            const ziel = zielSeiteFuer(d.y);
            const lage = seitenMass
              ? seiteNachVerschieben({ seite: eigeneSeite, top: bp.top }, d.y, seitenMass)
              : { seite: eigeneSeite, top: bp.top + d.y };
            // Ging der Zug über die letzte Seite hinaus, bleibt er auf ihr — dann gilt
            // die ungeteilte Höhe, damit der Block genau dort liegt, wo er losgelassen wurde.
            const top = lage.seite === ziel
              ? lage.top
              : bp.top + d.y - (ziel - eigeneSeite) * (seitenMass.pageH + seitenMass.randUnten + seitenMass.luecke + seitenMass.randOben);
            onOffsetChange(blockKey, { ...(base || {}), seite: ziel, top, left: bp.left + d.x });
          }
        }
      } else if (opts.onClickFallback) {
        // Ein Klick ohne Weg. Was er bedeutet, entscheidet die Aufrufstelle: auswählen,
        // in den Text gehen oder nichts. Von sich aus setzt ein Zug keinen Schreibcursor
        // mehr — in der Gruppe wäre das sogar falsch.
        opts.onClickFallback(mu);
      }
      setLiveDelta({ x: 0, y: 0 });
      liveDeltaRef.current = { x: 0, y: 0 };
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  };

  const startResize = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    const el = selfRef.current;
    if (!el || !base) return;
    // Der Inhalt sitzt im Stil-Wrapper, den wrap() erzeugt — dort steht die Schriftgröße.
    const inhalt = el.firstElementChild || el;
    const start = {
      top:    base.top  ?? 0,
      left:   base.left ?? 0,
      width:  el.offsetWidth,
      height: el.offsetHeight,
    };
    const startFont = Math.round(parseFloat(getComputedStyle(inhalt).fontSize)) || 14;
    // `tm` und `bm` stehen vorn wie die Ecken, sind aber keine: Sie ziehen nur die
    // Höhe und lassen Breite und Schrift in Ruhe.
    const istSenkrecht = handle[1] === 'm';
    const istEcke = (handle[0] === 't' || handle[0] === 'b') && !istSenkrecht;
    const sx = e.clientX, sy = e.clientY;
    // Wie beim Ziehen: Der Mausweg kommt in Bildschirmpunkten, gerechnet wird in
    // Papiermaßen. Ohne die Umrechnung wüchse der Block langsamer als die Maus.
    const skala = blattSkala();
    let letzte = null;

    // Die angefasste senkrechte Kante rastet an denselben Linien ein wie beim
    // Verschieben. Die Linien stehen während des Ziehens fest — die anderen Blöcke
    // rühren sich ja nicht.
    const container = el.closest('.anschreiben');
    const punkte = container
      ? snapPunkte(andereRects(container, [blockKey]), container.offsetWidth, container.offsetHeight).x
      : [];

    const onMove = (mv) => {
      const r = resizeMitEinrasten({
        griff: handle, start, punkte, toleranz: SNAP_THRESHOLD,
        delta: { x: (mv.clientX - sx) / skala, y: (mv.clientY - sy) / skala },
      });
      onSnapChange?.({ x: r.linie, y: null });
      pruefeLage({ left: r.left, top: r.top, width: r.width, height: r.hoehe });

      letzte = r;
      // Eckgriffe zeigen die Vorschau als gleichmäßige Skalierung um die feste Ecke.
      // Der Text bricht dabei nicht um — genau das ist beim Loslassen auch das Ergebnis,
      // weil Breite und Schrift im selben Verhältnis wachsen.
      setLiveResize(istEcke
        ? { top: start.top, left: start.left, width: start.width, skala: r.skala, origin: ANKER_ORIGIN[handle] }
        : { top: r.top, left: r.left, width: r.width, skala: 1, ...(istSenkrecht ? { hoehe: r.hoehe } : {}) });
    };
    const onUp = (mu) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      onSnapChange?.({ x: null, y: null });
      const dx = mu.clientX - sx, dy = mu.clientY - sy;
      if (letzte && (Math.abs(dx) > 1 || Math.abs(dy) > 1)) {
        // Die Höhe wird nur von den waagerechten Griffen gesetzt und wirkt als
        // Mindesthöhe — so kann ein zu kleiner Kasten niemals Text verschlucken.
        const position = {
          ...base,
          top: letzte.top,
          left: letzte.left,
          width: letzte.width,
          ...(istSenkrecht ? { hoehe: letzte.hoehe } : {}),
        };
        // Position und Schriftgröße müssen in EINEM Patch gehen: der Empfänger baut
        // jeden Patch auf dem Stand vor dem Rendern auf, zwei Aufrufe würden sich
        // gegenseitig überschreiben.
        const neueGroesse = Math.min(MAX_FONT, Math.max(MIN_FONT, Math.round(startFont * letzte.skala)));
        const stilPatch = (istEcke && neueGroesse !== startFont)
          ? { [blockKey]: { ...(blockStyles?.[blockKey] || {}), fontSize: neueGroesse } }
          : undefined;
        onOffsetChange(blockKey, position, stilPatch);
      }
      setLiveResize(null);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  };

  // Den Schreibcursor an die angeklickte Stelle setzen. Trifft der Klick keinen Text —
  // etwa neben die letzte Zeile —, wird das senkrecht nächstgelegene Textfeld genommen.
  const schreibcursorSetzen = (x, y) => {
    const editables = [...(selfRef.current?.querySelectorAll('[contenteditable], textarea, input:not([type="hidden"])') ?? [])];
    if (!editables.length) return;
    const nearest = editables.reduce((best, el) => {
      const mid = (el.getBoundingClientRect().top + el.getBoundingClientRect().bottom) / 2;
      const bm  = (best.getBoundingClientRect().top + best.getBoundingClientRect().bottom) / 2;
      return Math.abs(mid - y) < Math.abs(bm - y) ? el : best;
    }, editables[0]);
    nearest.focus();
    try {
      const range = document.caretRangeFromPoint?.(x, y);
      if (range && selfRef.current?.contains(range.startContainer)) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } catch (_) {}
  };

  // Der Weg in den Text. Er geht erst nach dem Render, weil die Textfelder bis dahin
  // noch keine Zeigerereignisse annehmen und der Browser den Cursor nicht setzen könnte.
  const inDenTextGehen = (x, y) => {
    setImText(true);
    requestAnimationFrame(() => schreibcursorSetzen(x, y));
  };

  const handleBlockMouseDown = (e) => {
    if (e.target.closest('button, .an-resize-handle, .an-block-toolbar')) return;
    // Umschalt-Klick sammelt nur ein und zieht nicht: Ein Zug würde sonst noch mit der
    // Auswahl von vor dem Klick rechnen und den falschen Block mitnehmen.
    if (e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(blockKey, true);
      return;
    }
    // In einer Gruppe ist der ganze Block Ziehfläche; zum Schreiben erst einzeln wählen.
    if (gruppe) {
      if (document.activeElement?.isContentEditable) document.activeElement.blur();
      startDrag(e);
      return;
    }
    // Im Text macht der Zeiger nur noch das, was er in jedem Textfeld tut: Cursor setzen
    // und markieren. Nichts hier darf ihm dazwischenfunken — genau daran scheiterte das
    // Markieren bisher, weil jeder Zug den Block mitnahm.
    if (imText) {
      e.stopPropagation();
      return;
    }
    // Steht der Schreibcursor noch in einem anderen Block, muss er dort weg. Sonst
    // bewegen die Pfeiltasten weiterhin ihn statt des neu gewählten Blocks — und das
    // Verlassen des Textfeldes ist zugleich der Moment, in dem es gespeichert wird.
    if (document.activeElement?.isContentEditable && !selfRef.current?.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    // Erster Klick wählt nur aus, ein zweiter geht in den Text. Entschieden wird das erst
    // beim Loslassen: Bis dahin kann aus dem Klick noch ein Zug werden, und der soll den
    // Block verschieben, nicht den Cursor setzen.
    startDrag(e, isSelected
      ? { skipSelect: true, onClickFallback: (mu) => inDenTextGehen(mu.clientX, mu.clientY) }
      : { onClickFallback: () => {} });
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const active = document.activeElement;
    if (!active?.isContentEditable || !selfRef.current?.contains(active)) return;
    const editables = [...selfRef.current.querySelectorAll('[contenteditable]')];
    const idx = editables.indexOf(active);
    if (idx === -1) return;
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const caretRect = sel.getRangeAt(0).getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const lh = parseFloat(getComputedStyle(active).lineHeight) || 20;
    if (e.key === 'ArrowDown' && idx < editables.length - 1) {
      if (caretRect.bottom < activeRect.bottom - lh * 0.5) return;
      e.preventDefault();
      const next = editables[idx + 1];
      next.focus();
      const r = document.createRange();
      r.setStart(next.firstChild ?? next, 0);
      r.collapse(true);
      sel.removeAllRanges(); sel.addRange(r);
    } else if (e.key === 'ArrowUp' && idx > 0) {
      if (caretRect.top > activeRect.top + lh * 0.5) return;
      e.preventDefault();
      const prev = editables[idx - 1];
      prev.focus();
      const r = document.createRange();
      r.selectNodeContents(prev);
      r.collapse(false);
      sel.removeAllRanges(); sel.addRange(r);
    }
  };

  // Doppelklick führt aus dem Ruhezustand in einem Zug in den Text — auch das kennt man
  // von Canva. Sind wir schon drin, gehört der Doppelklick dem Browser: Er wählt damit
  // ein Wort aus.
  const handleDblClick = (e) => {
    e.stopPropagation();
    if (imText || gruppe) return;
    inDenTextGehen(e.clientX, e.clientY);
  };

  const isRightAligned = blockKey === 'absender' || blockKey === 'datum';
  // Adressblöcke sind nur so breit wie ihr Text. Beim Absender und beim Datum ergab sich
  // das schon aus dem rechten Rand; der Empfänger stand links und bekam deshalb die
  // volle Spaltenbreite — sein Rahmen war ein Vielfaches seiner drei Zeilen.
  const passtSichDemTextAn = isRightAligned || blockKey === 'empfaenger';
  const effectiveWidth = liveResize?.width ?? base?.width;

  // In der Gruppe kommt der Versatz von außen, damit alle Blöcke denselben nehmen.
  const zug = gruppenZug?.keys?.includes(blockKey) ? gruppenZug.delta : liveDelta;

  // Eine gesetzte Höhe wirkt als MINDESTHöhe, nie als feste. Wächst der Text über den
  // Kasten hinaus, wächst der Kasten mit — ein Textfeld, das seinen eigenen Inhalt
  // abschneidet, wäre eine Falle, und im PDF wäre der Text dann einfach fort.
  const effectiveHoehe = liveResize?.hoehe ?? base?.hoehe;

  const posTop  = liveResize ? liveResize.top  : base?.top  + zug.y;
  const posLeft = liveResize ? liveResize.left : base?.left + zug.x;

  // Im Bearbeitungsmodus sitzt der Block über `transform` an seinem Platz, nicht über
  // `top`/`left`.
  //
  // Der Grund ist das Wackeln, das der User am 2026-08-11 gemeldet hat: „die Buchstaben
  // sind nicht fest verankert, manche ziehen mit einem Punkt Verzögerung nach" — beim
  // Ziehen mit der Maus genauso wie beim Verschieben mit den Pfeiltasten.
  //
  // `top`/`left` sind Layout-Werte. Ändert sich einer, baut der Browser den Block neu auf
  // und setzt dabei jede Textzeile einzeln auf ganze Bildpunkte. Weil das Blatt
  // verkleinert angezeigt wird, sitzt jede Zeile an einer anderen Nachkommastelle: Die
  // eine rundet schon auf, die andere noch ab. Der Block wandert also völlig richtig, nur
  // brechen seine Zeilen dabei aus der Reihe. Mit ganzen Zahlen ist dagegen nichts
  // auszurichten — bei 46 % Anzeige und einem Bildschirmfaktor von 1,1 ist ein Schritt
  // 0,507 Bildpunkte, und keine noch so gewählte Schrittweite trifft damit je genau auf
  // ganze Punkte.
  //
  // `transform` umgeht das Problem statt es zu rechnen: Der Block wird einmal gezeichnet
  // und danach als fertiges Bild verschoben. Alle Zeilen behalten ihren Abstand
  // zueinander auf den Bruchteil genau, weil gar nichts neu umbrochen oder neu gerundet
  // wird.
  //
  // Nur im Bearbeitungsmodus. In der reinen Anzeige bewegt sich nichts, dort gibt es
  // nichts zu gewinnen — und aus genau dieser Anzeige entsteht das PDF, das deshalb
  // unangetastet auf `top`/`left` bleibt.
  const perTransform = interaktiv && base != null;
  const transformTeile = [
    perTransform ? `translate3d(${posLeft}px, ${posTop}px, 0)` : null,
    liveResize?.skala && liveResize.skala !== 1 ? `scale(${liveResize.skala})` : null,
  ].filter(Boolean);

  const style = base ? {
    position: 'absolute',
    top:  (perTransform ? 0 : posTop)  + 'px',
    left: (perTransform ? 0 : posLeft) + 'px',
    ...(effectiveHoehe != null ? { minHeight: effectiveHoehe + 'px' } : {}),
    ...(effectiveWidth != null
      ? { width: effectiveWidth + 'px', boxSizing: 'border-box' }
      // `max-content` statt `fit-content`: Bei einem absolut gesetzten Kasten rechnet
      // `fit-content` mit dem Platz, der rechts vom Block noch übrig ist. Zieht man ihn
      // nach rechts, schrumpft dieser Platz — und die Hausnummer sprang plötzlich in die
      // nächste Zeile. `max-content` hängt nur am Text, nie an der Position.
      : passtSichDemTextAn
        ? { width: 'max-content', maxWidth: '100%' }
        : { width: '100%', boxSizing: 'border-box' }
    ),
    ...(transformTeile.length
      ? {
          transform: transformTeile.join(' '),
          ...(liveResize?.origin ? { transformOrigin: liveResize.origin } : {}),
        }
      : {}),
    // Ohne diese Zeile wirkt der ganze Umbau nicht verlässlich: Der Browser darf einen
    // Block, den er nur verschoben glaubt, trotzdem neu zeichnen — und dann rundet er
    // die Zeilen wieder einzeln. `will-change` sagt ihm verbindlich, dass sich hier nur
    // die Lage ändert; er legt den Block auf eine eigene Zeichenebene und schiebt ihn
    // als Ganzes. Nur im Bearbeitungsmodus, und dort sind es eine Handvoll Blöcke.
    ...(perTransform ? { willChange: 'transform' } : {}),
  } : undefined;

  const cls = [
    'an-drag-block',
    !interaktiv && 'an-drag-block--statisch',
    geparkt && 'an-geparkt',
    interaktiv && (isSelected || imBund) && 'an-selected',
    interaktiv && imText && 'an-im-text',
    interaktiv && isSelected && (imText ? 'an-zeigt-text' : 'an-zeigt-move'),
    // Im Verbund gibt es keinen Schreibcursor — der ganze Block ist Ziehfläche.
    interaktiv && imBund && 'an-zeigt-move',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={selfRef}
      className={cls}
      style={style}
      data-block-key={blockKey}
      onKeyDown={interaktiv ? handleKeyDown : undefined}
      onDoubleClick={interaktiv ? handleDblClick : undefined}
      onPointerDown={interaktiv ? handleBlockMouseDown : undefined}
      onClick={interaktiv ? (e => e.stopPropagation()) : undefined}
    >
      {children}
      {interaktiv && isSelected && RESIZE_HANDLES.map(h => (
        <div key={h} className={`an-resize-handle an-resize-handle--${h}`} onPointerDown={(e) => startResize(e, h)} />
      ))}
    </div>
  );
}

// ── Anschreiben Template ──────────────────────────────────────

function AnschreibenLayout({
  rohtext,
  nutzer,
  stelle,
  template,
  onEditToggle,
  onInlineChange,
  onAbsenderToggle,
  onEmpfaengerToggle,
  onDatumToggle,
  onAbsenderInlineChange,
  onEmpfaengerInlineChange,
  onDatumInlineChange,
  activeEditKey,
  onActiveEditKeyChange,
  blockOffsets,
  onOffsetChange,
  selectedBlock,
  onSelectBlock,
  auswahl,
  onGruppeVerschieben,
  blatt,
  sicher,
  onRandVerletzt,
  seitenMass,
  letzteSeite,
  onZug,
  onStylePatch,
  onKiEdit,
  blockStyles,
  blockHtml,
  imFluss,
  seite,
  eigene = [],
  onEigenesFeldChange,
}) {
  const [activeSnap, setActiveSnap] = useState({ x: null, y: null });
  // Läuft gerade ein gemeinsamer Zug, steht hier der Versatz für alle beteiligten
  // Blöcke — eine Stelle für alle, damit sie nicht auseinanderlaufen.
  const [gruppenZug, setGruppenZug] = useState(null);
  const { betreff, betreffRohzeile, anrede, paragraphen, gruss, grussRohzeile, unterschriftZeilen } = parseAnschreiben(rohtext);

  const datumSrc = stelle?.brief_datum || stelle?.generiert_am || null;
  const datum = datumSrc
    ? new Date(datumSrc).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  const city = stelle?.brief_ort || nutzer?.ort || nutzer?.plz_ort?.split(' ').slice(1).join(' ') || '';
  const datumZeile = stelle?.brief_datum_anzeige || (city ? `${city}, ${datum}` : datum);

  const absenderDaten = buildAnschreibenAbsender(stelle, nutzer);
  const vollName = absenderDaten.name || `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const absenderTeile = anschreibenAbsenderZeilen(stelle, nutzer);
  const editStateProps = (key) => (
    onActiveEditKeyChange
      ? {
          onMouseEnter: () => onActiveEditKeyChange(key),
          onMouseLeave: () => onActiveEditKeyChange(null),
          onFocus: () => onActiveEditKeyChange(key),
          onBlur: () => onActiveEditKeyChange(null),
        }
      : {}
  );
  const editableClassName = (key, extraClass = '') => [
    extraClass,
    activeEditKey === key ? 'is-active' : '',
  ].filter(Boolean).join(' ');

  const wrap = (key, node) => {
    // Jeder Block gehört zu genau einer Seite und wird nur auf ihr gezeichnet. Das
    // ersetzt den früheren Trick, auf jeder Seite denselben Brief verschoben zu zeigen —
    // dabei überlappten sich benachbarte Seiten um oberen plus unteren Rand, und derselbe
    // Streifen erschien zweimal.
    // Fehlt die Angabe, ist es Seite 0: So bleiben Briefe lesbar, die vor der Umstellung
    // gespeichert wurden, auch bevor die Umrechnung gelaufen ist.
    // Der Messklon (`imFluss`) kennt keine Seiten — er misst den natürlichen Fluss.
    if (!imFluss && seite != null && (blockOffsets?.[key]?.seite ?? 0) !== seite) return null;
    // Ausgeblendete Blöcke verschwinden aus dem Brief (und damit auch aus dem PDF, das
    // aus derselben Anzeige entsteht). Im Messklon bleiben sie, sonst würden die
    // Startpositionen aller anderen Blöcke nachrutschen. Beim Bearbeiten bleibt an ihrer
    // Stelle eine Spur stehen: Ohne sie wüsste niemand, dass dort etwas fehlt, und der
    // Weg zurück führte nur über blindes Rückgängigmachen.
    const versteckt = blockOffsets?.[key];
    if (!imFluss && versteckt?.versteckt) {
      if (!onOffsetChange) return null;
      // Die Spur nimmt Platz UND Maße des echten Blocks an. Damit ergeben mehrere Spuren
      // zusammen das Gerippe eines Anschreibens — man erkennt allein an den Flächen,
      // was fehlt und wo es stand. Die Höhe wurde beim Ausblenden gemessen; nur für
      // Briefe von davor greifen die Ersatzmaße.
      const ersatz = AN_SPUR_ERSATZ[key] || { breite: 0.5, hoehe: 40 };
      return (
        <button
          key={key}
          type="button"
          className="an-versteckt-spur"
          style={{
            position: 'absolute',
            top: versteckt.top + 'px',
            left: versteckt.left + 'px',
            width: versteckt.width != null ? versteckt.width + 'px' : `${ersatz.breite * 100}%`,
            height: (versteckt.spurHoehe ?? versteckt.hoehe ?? ersatz.hoehe) + 'px',
          }}
          title={`${AN_BLOCK_NAME[key] || 'Block'} wieder einblenden`}
          onClick={(e) => { e.stopPropagation(); onOffsetChange(key, { ...versteckt, versteckt: false }); }}
        >
          <span className="an-versteckt-spur-name">{AN_BLOCK_NAME[key] || 'Block'}</span>
        </button>
      );
    }
    const bs = blockStyles?.[key] || {};
    const blockStyle = { textAlign: bs.textAlign || AN_START_AUSRICHTUNG[key] || 'left' };
    // Blöcke mit eigener Schriftvorgabe im Stylesheet (z. B. der Betreff) erben nicht.
    // Deshalb zusätzlich als Variable weitergeben — das Stylesheet nutzt sie als Vorgabe.
    if (bs.fontSize) {
      blockStyle.fontSize = bs.fontSize + 'px';
      blockStyle['--an-block-font-size'] = bs.fontSize + 'px';
    }
    if (bs.fontFamily) {
      blockStyle.fontFamily = bs.fontFamily;
      blockStyle['--an-block-font-family'] = bs.fontFamily;
    }
    if (bs.color) blockStyle.color = bs.color;
    if (imFluss) {
      return (
        <div className="an-fluss-block" data-block-key={key}>
          {Object.keys(blockStyle).length > 0 ? <div style={blockStyle}>{node}</div> : node}
        </div>
      );
    }
    return (
      <DragBlock
        key={key}
        blockKey={key}
        offsets={blockOffsets}
        onOffsetChange={onOffsetChange}
        isSelected={selectedBlock === key}
        onSelect={onSelectBlock}
        onSnapChange={setActiveSnap}
        onStylePatch={onStylePatch}
        onKiEdit={onKiEdit}
        blockStyles={blockStyles}
        auswahl={auswahl}
        gruppenZug={gruppenZug}
        onGruppenZug={setGruppenZug}
        onGruppeVerschieben={onGruppeVerschieben}
        blatt={blatt}
        sicher={sicher}
        onRandVerletzt={onRandVerletzt}
        seitenMass={seitenMass}
        letzteSeite={letzteSeite}
        onZug={onZug}
      >
        {Object.keys(blockStyle).length > 0
          ? <div style={blockStyle}>{node}</div>
          : node}
      </DragBlock>
    );
  };

  // Kein Abwählen bei Klick auf freie Fläche an dieser Stelle: Das erledigt der äußere
  // Kasten `an-paginas-outer`, und zwar mit der Sperre, die einen frisch aufgezogenen
  // Auswahlrahmen nicht sofort wieder löscht. Solange dieser Kasten die Höhe null hatte,
  // war der Klick hier ohnehin unerreichbar.
  return (
    <div className={`anschreiben an-${template}`}>
      {activeSnap.x != null && (
        <div className="an-snap-line an-snap-line-v" style={{ left: activeSnap.x + 'px' }} />
      )}
      {activeSnap.y != null && (
        <div className="an-snap-line an-snap-line-h" style={{ top: activeSnap.y + 'px' }} />
      )}
      {activeSnap.gleich && [activeSnap.gleich.oben, activeSnap.gleich.unten].map((l, i) => (
        <div
          key={i}
          className="an-snap-abstand"
          style={{ left: activeSnap.gleich.x + 'px', top: l.von + 'px', height: (l.bis - l.von) + 'px' }}
        />
      ))}
      {wrap('absender',
        <div className="an-absender-wrapper" {...editStateProps('absender')}>
          {(onAbsenderInlineChange && onOffsetChange)
            ? <InlineText key={absenderTeile.join('\n')} value={absenderTeile.join('\n')} rohzeile={absenderTeile.join('\n')} className="an-absender an-absender-inline" onChange={(_, v, hp) => onAbsenderInlineChange(v, hp)} multiLine={true} htmlValue={blockHtml?.['absender']} itemKey="absender" />
            : (blockHtml?.['absender']?.html && blockHtml.absender.text === absenderTeile.join('\n'))
              ? <div className="an-absender" dangerouslySetInnerHTML={{ __html: blockHtml.absender.html.replace(/\n/g, '<br>') }} />
              : <div className="an-absender">{absenderTeile.map((teil, i) => <div key={i}>{teil}</div>)}</div>
          }
          {onAbsenderToggle && !onAbsenderInlineChange && (
            <button className="an-edit-btn an-absender-edit-btn" onClick={onAbsenderToggle}>
              <EditPencilIcon size={13} />
            </button>
          )}
        </div>
      )}

      {wrap('empfaenger',
        <div className="an-empfaenger-wrapper" {...editStateProps('empfaenger')}>
          {(() => {
            const zeilen = [
              echterFirmenname(stelle),
              stelle?.empfaenger_ansprechperson || '',
              stelle?.empfaenger_strasse || '',
              stelle?.empfaenger_plz_ort || '',
              stelle?.job_id ? `Job-ID: ${stelle.job_id}` : '',
            ].filter(Boolean);
            const empfFirma = zeilen[0] || '';
            const empfRest = zeilen.slice(1).join('\n');
            return (onEmpfaengerInlineChange && onOffsetChange)
              ? <div className="an-empfaenger">
                  <InlineText key={empfFirma} value={empfFirma} rohzeile={empfFirma} className="an-empfaenger-firma" multiLine={false} onChange={(_, v, hp) => onEmpfaengerInlineChange([v, ...zeilen.slice(1)].join('\n'), hp, 'empfaenger_firma')} htmlValue={blockHtml?.['empfaenger_firma']} itemKey="empfaenger_firma" />
                  {zeilen.length > 1 && <InlineText key={empfRest} value={empfRest} rohzeile={empfRest} className="an-empfaenger-inline" multiLine={true} onChange={(_, v, hp) => onEmpfaengerInlineChange([zeilen[0], v].join('\n'), hp, 'empfaenger_rest')} htmlValue={blockHtml?.['empfaenger_rest']} itemKey="empfaenger_rest" />}
                </div>
              : <div className="an-empfaenger">
                  {(blockHtml?.['empfaenger_firma']?.html && blockHtml.empfaenger_firma.text === empfFirma)
                    ? <div className="an-empfaenger-firma" dangerouslySetInnerHTML={{ __html: blockHtml.empfaenger_firma.html }} />
                    : empfFirma ? <div className="an-empfaenger-firma">{empfFirma}</div> : null}
                  {(blockHtml?.['empfaenger_rest']?.html && blockHtml.empfaenger_rest.text === empfRest)
                    ? <div dangerouslySetInnerHTML={{ __html: blockHtml.empfaenger_rest.html.replace(/\n/g, '<br>') }} />
                    : <>
                        {stelle?.empfaenger_ansprechperson && <div>{stelle.empfaenger_ansprechperson}</div>}
                        {stelle?.empfaenger_strasse && <div>{stelle.empfaenger_strasse}</div>}
                        {stelle?.empfaenger_plz_ort && <div>{stelle.empfaenger_plz_ort}</div>}
                        {stelle?.job_id && <div>Job-ID: {stelle.job_id}</div>}
                      </>}
                </div>;
          })()}
          {onEmpfaengerToggle && !onEmpfaengerInlineChange && (
            <button className="an-edit-btn an-empfaenger-edit-btn" onClick={onEmpfaengerToggle}>
              <EditPencilIcon size={13} />
            </button>
          )}
        </div>
      )}

      {wrap('datum',
        <div className="an-datum" {...editStateProps('datum')}>
          {(onDatumInlineChange && onOffsetChange)
            ? <InlineText key={datumZeile} value={datumZeile} rohzeile={datumZeile} className="an-datum-inline" onChange={(_, v, hp) => onDatumInlineChange(v, hp)} multiLine={false} htmlValue={blockHtml?.['datum']} itemKey="datum" />
            : (blockHtml?.['datum']?.html && blockHtml.datum.text === datumZeile)
              ? <span dangerouslySetInnerHTML={{ __html: blockHtml.datum.html }} />
              : datumZeile
          }
          {onDatumToggle && !onDatumInlineChange && (
            <button className="an-edit-btn an-datum-edit-btn" onClick={onDatumToggle}>
              <EditPencilIcon size={13} />
            </button>
          )}
        </div>
      )}

      {betreff && wrap('betreff',
        onInlineChange
          ? <EditableText key={betreff} value={betreff} rohzeile={betreffRohzeile} className="an-betreff" onChange={onInlineChange} itemKey="betreff" htmlValue={blockHtml?.['betreff']} />
          : (blockHtml?.betreff?.html && blockHtml.betreff.text === betreff)
            ? <div className="an-betreff" dangerouslySetInnerHTML={{ __html: blockHtml.betreff.html }} />
            : <div className="an-betreff">{betreff}</div>
      )}

      {wrap('text', (() => {
        const { rawBody, prefix } = brieftextAusRohtext(rohtext, betreffRohzeile, vollName);

        if (onInlineChange) {
          const handleBodyChange = (_, newBody, htmlPayload) => {
            onInlineChange(rohtext, rohtextMitBrieftext(prefix, newBody), htmlPayload);
          };
          // Der Messklon bekommt dasselbe Element, aber keinen Schreibweg. Dasselbe
          // Element, damit die Messung stimmt — ein anderer Kasten misst sich anders.
          // Keinen Schreibweg, weil er unsichtbar ist und beim Speichern eine leere
          // Zeichenkette liefern würde.
          return <BodyEditable rawBody={rawBody} htmlBody={blockHtml?.['text']} onChange={imFluss ? undefined : handleBodyChange} />;
        }

        const textHtmlOk = blockHtml?.['text']?.html && blockHtml['text'].text === rawBody
          && (!rawBody.includes('\n') || blockHtml['text'].html.includes('<br') || blockHtml['text'].html.includes('\n'));
        return textHtmlOk
          ? <div className="an-body-static" dangerouslySetInnerHTML={{ __html: blockHtml['text'].html.replace(/\n/g, '<br>') }} />
          : <div className="an-body-static" style={{ whiteSpace: 'pre-wrap' }}>{rawBody.replace(/\*\*/g, '')}</div>;
      })())}

      {/* Frei angelegte Textfelder. Sie laufen durch dasselbe `wrap` wie die festen
          Blöcke und sind damit ohne Zutun ziehbar, skalierbar, einrastend und
          formatierbar — es gibt bewusst keine zweite Sorte Block.
          Im Messklon haben sie nichts verloren: Sie stehen frei auf der Seite und
          würden dort die Startpositionen der festen Blöcke nach unten schieben. */}
      {!imFluss && eigene.map((id) => wrap(id, (
        onEigenesFeldChange
          ? <BodyEditable
              key={id}
              itemKey={id}
              rawBody={blockHtml?.[id]?.text || ''}
              htmlBody={blockHtml?.[id]}
              onChange={(_, plain, hp) => onEigenesFeldChange(id, plain, hp)}
            />
          : <div className="an-body-static" style={{ whiteSpace: 'pre-wrap' }}>
              {blockHtml?.[id]?.html
                ? <span dangerouslySetInnerHTML={{ __html: blockHtml[id].html.replace(/\n/g, '<br>') }} />
                : (blockHtml?.[id]?.text || '')}
            </div>
      )))}
    </div>
  );
}

// ── Haupt-Component ───────────────────────────────────────────

const AN_FONTS = [
  { label: 'Standard', value: '' },
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica Neue, Helvetica, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Open Sans', value: 'Open Sans, sans-serif' },
  { label: 'Lato', value: 'Lato, sans-serif' },
  { label: 'Montserrat', value: 'Montserrat, sans-serif' },
  { label: 'Source Sans', value: 'Source Sans 3, sans-serif' },
  { label: 'Nunito', value: 'Nunito, sans-serif' },
  { label: 'Raleway', value: 'Raleway, sans-serif' },
  { label: 'Poppins', value: 'Poppins, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: 'Times New Roman, serif' },
  { label: 'Garamond', value: 'Garamond, serif' },
  { label: 'Palatino', value: 'Palatino, serif' },
  { label: 'Merriweather', value: 'Merriweather, serif' },
  { label: 'Playfair Display', value: 'Playfair Display, serif' },
  { label: 'EB Garamond', value: 'EB Garamond, serif' },
  { label: 'Libre Baskerville', value: 'Libre Baskerville, serif' },
  { label: 'PT Serif', value: 'PT Serif, serif' },
];

// Content area per A4 page: 297mm − 20mm top − 20mm bottom = 257mm
// Alle nicht-leeren Textknoten, die von der Markierung berührt werden.
function textknotenImBereich(range) {
  const c = range.commonAncestorContainer;
  const root = c.nodeType === Node.TEXT_NODE ? c.parentElement : c;
  if (!root) return [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const out = [];
  let n;
  while ((n = walker.nextNode())) {
    if (!n.textContent.trim()) continue;
    if (range.intersectsNode(n)) out.push(n);
  }
  return out;
}

// Liefert den Wert nur, wenn er über die ganze Markierung gleich ist — sonst `null`.
// Uneinheitlich heißt: Knopf nicht aktiv, statt fälschlich aktiv.
function einheitlicherWert(elemente, lies) {
  if (!elemente.length) return null;
  const erster = lies(elemente[0]);
  return elemente.every(el => lies(el) === erster) ? erster : null;
}

/**
 * Schriftauswahl mit Vorschau beim Überfahren.
 * Ein natives Auswahlfeld meldet nicht, über welchem Eintrag der Zeiger steht —
 * deshalb eine eigene Liste. `onVorschau(wert)` zeigt live, `onVorschau(null)`
 * nimmt zurück, `onWahl` übernimmt endgültig.
 */
function AnFontPicker({ value, disabled, onVorschau, onWahl }) {
  const [offen, setOffen] = useState(false);
  const [ueberfahren, setUeberfahren] = useState(null);
  const wurzelRef = useRef(null);

  const schliessen = useCallback(() => {
    setOffen(false);
    setUeberfahren(null);
    onVorschau(null);
  }, [onVorschau]);

  useEffect(() => {
    if (!offen) return;
    const aussen = (e) => { if (!wurzelRef.current?.contains(e.target)) schliessen(); };
    const escape = (e) => { if (e.key === 'Escape') schliessen(); };
    document.addEventListener('pointerdown', aussen);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', aussen);
      document.removeEventListener('keydown', escape);
    };
  }, [offen, schliessen]);

  const aktuell = AN_FONTS.find(f => f.value === (value || '')) || AN_FONTS[0];
  const angezeigt = ueberfahren != null
    ? (AN_FONTS.find(f => f.value === ueberfahren) || aktuell)
    : aktuell;

  return (
    <div className="an-font-picker" ref={wurzelRef}>
      <button
        type="button"
        className="an-font-picker-btn"
        disabled={disabled}
        title="Schriftart"
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => (offen ? schliessen() : setOffen(true))}
      >
        <span style={{ fontFamily: angezeigt.value || undefined }}>{angezeigt.label}</span>
        <span className="an-font-picker-pfeil">▾</span>
      </button>

      {offen && (
        <div
          className="an-font-picker-liste"
          onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
          onPointerLeave={() => { setUeberfahren(null); onVorschau(null); }}
        >
          {AN_FONTS.map(f => (
            <button
              type="button"
              key={f.value}
              className={`an-font-picker-eintrag${f.value === (value || '') ? ' is-active' : ''}`}
              style={{ fontFamily: f.value || undefined }}
              onPointerEnter={() => { setUeberfahren(f.value); onVorschau(f.value); }}
              onClick={() => { onWahl(f.value); schliessen(); }}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Griff, Körper und Abdruckfläche — als Stempel erkennbar, nicht als Pfeilkonstrukt.
const StempelIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9.6 2.5h4.8a1.4 1.4 0 0 1 1.39 1.62l-.83 5.2a1.6 1.6 0 0 0 .74 1.63l1.72 1.05a2.9 2.9 0 0 1 1.38 2.47V15.5H5.2v-1.03a2.9 2.9 0 0 1 1.38-2.47l1.72-1.05a1.6 1.6 0 0 0 .74-1.63l-.83-5.2A1.4 1.4 0 0 1 9.6 2.5Z" />
    <rect x="4.2" y="17.6" width="15.6" height="3.2" rx="1.4" />
  </svg>
);

// Wenige, für einen Brief sinnvolle Farben statt der vollen Farbwahl des Systems.
const AN_FARBEN = [
  { label: 'Standard',   value: '' },
  { label: 'Schwarz',    value: '#111111' },
  { label: 'Dunkelgrau', value: '#444444' },
  { label: 'Grau',       value: '#767676' },
  { label: 'Teal',       value: '#004D40' },
  { label: 'Blau',       value: '#1A4E8A' },
  { label: 'Bordeaux',   value: '#8A1A2E' },
  { label: 'Braun',      value: '#6B4A2B' },
];

/** Farbwahl: Farbfläche, Farbtonregler, Hex-Eingabe und schnelle Grundtöne. */
function AnColorPicker({ value, disabled, onWahl }) {
  const [offen, setOffen] = useState(false);
  const wurzelRef = useRef(null);
  const flaecheRef = useRef(null);
  const [hsv, setHsv] = useState(() => hexNachHsv(value || '#111111'));
  const [hexFeld, setHexFeld] = useState(value || '#111111');

  // Beim Öffnen den gespeicherten Wert übernehmen.
  useEffect(() => {
    if (!offen) return;
    setHsv(hexNachHsv(value || '#111111'));
    setHexFeld((value || '#111111').toUpperCase());
  }, [offen, value]);

  const setzeAusHsv = (next, uebernehmen) => {
    setHsv(next);
    const hex = hsvNachHex(next.h, next.s, next.v);
    setHexFeld(hex.toUpperCase());
    if (uebernehmen) onWahl(hex);
  };

  // Ziehen in der Farbfläche: waagerecht die Sättigung, senkrecht die Helligkeit.
  const flaecheZiehen = (e) => {
    const el = flaecheRef.current;
    if (!el) return;
    const rechteck = el.getBoundingClientRect();
    const lies = (ev) => {
      const s = Math.min(1, Math.max(0, (ev.clientX - rechteck.left) / rechteck.width));
      const v = Math.min(1, Math.max(0, 1 - (ev.clientY - rechteck.top) / rechteck.height));
      return { h: hsv.h, s, v };
    };
    setzeAusHsv(lies(e), false);
    const bewegen = (ev) => setzeAusHsv(lies(ev), false);
    const los = (ev) => {
      document.removeEventListener('pointermove', bewegen);
      document.removeEventListener('pointerup', los);
      setzeAusHsv(lies(ev), true); // gespeichert wird erst beim Loslassen
    };
    document.addEventListener('pointermove', bewegen);
    document.addEventListener('pointerup', los);
  };

  useEffect(() => {
    if (!offen) return;
    const aussen = (e) => { if (!wurzelRef.current?.contains(e.target)) setOffen(false); };
    const escape = (e) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('pointerdown', aussen);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', aussen);
      document.removeEventListener('keydown', escape);
    };
  }, [offen]);

  const aktuell = value || '#111111';

  return (
    <div className="an-font-picker" ref={wurzelRef}>
      <button
        type="button"
        className="an-block-toolbar-btn"
        disabled={disabled}
        title="Textfarbe"
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => setOffen(o => !o)}
      >
        <span style={{ fontSize: 15, fontWeight: 700, color: aktuell,
                       textDecoration: `underline 3px solid ${aktuell}` }}>A</span>
      </button>

      {offen && (
        <div
          className="an-farb-panel"
          onPointerDown={e => e.stopPropagation()}
        >
          <div
            ref={flaecheRef}
            className="an-farb-flaeche"
            style={{ '--an-farbton': `hsl(${hsv.h}, 100%, 50%)` }}
            onPointerDown={flaecheZiehen}
          >
            <span
              className="an-farb-zeiger"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
            />
          </div>

          <input
            type="range"
            className="an-farb-ton"
            min="0" max="359" step="1"
            value={Math.round(hsv.h)}
            onChange={e => setzeAusHsv({ ...hsv, h: Number(e.target.value) }, false)}
            onPointerUp={() => onWahl(hsvNachHex(hsv.h, hsv.s, hsv.v))}
            onKeyUp={() => onWahl(hsvNachHex(hsv.h, hsv.s, hsv.v))}
          />

          <div className="an-farb-zeile">
            <span className="an-farb-probe" style={{ background: hexFeld }} />
            <input
              className="an-farb-hex"
              value={hexFeld}
              spellCheck={false}
              onChange={e => {
                const roh = e.target.value;
                setHexFeld(roh);
                if (/^#?[0-9a-f]{6}$/i.test(roh.trim())) {
                  const hex = roh.trim().startsWith('#') ? roh.trim() : '#' + roh.trim();
                  setHsv(hexNachHsv(hex));
                  onWahl(hex);
                }
              }}
            />
          </div>

          <div className="an-farb-schnell">
            {AN_FARBEN.map(f => (
              <button
                type="button"
                key={f.value}
                className={`an-farb-feld${f.value === (value || '') ? ' is-active' : ''}`}
                title={f.label}
                style={{ background: f.value || '#111111' }}
                onClick={() => { onWahl(f.value); setOffen(false); }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AnFixedToolbar({ selectedBlock, blockStyles, fmtState, onStylePatch, onFontVorschau, stempelAktiv, onStempel, onTextfeld }) {
  // Jeder Block des Briefes trägt Text — auch die frei angelegten Felder. Die frühere
  // Aufzählung der fünf festen Blöcke hätte ein neues Feld von Schrift, Farbe und
  // Ausrichtung ausgeschlossen.
  const isTextBlock = !!selectedBlock;
  const bs = blockStyles?.[selectedBlock] || {};
  const currentAlign = bs.textAlign || AN_START_AUSRICHTUNG[selectedBlock] || 'left';
  // Die eigene Vorgabe des Blocks zählt zuerst. fmtState ist nur eine Messung am
  // Bildschirm und wird erst bei der nächsten Markierungsänderung neu gelesen —
  // stünde sie vorn, zeigte die Anzeige nach dem ersten Druck weiter den alten Wert
  // und die nächste Rechnung ginge wieder vom alten Wert aus.
  const aktuelleGroesse = bs.fontSize ?? fmtState?.fontSize ?? 14;
  // Am Farbregler oder an der Größe zu ziehen erzeugt viele Änderungen hintereinander.
  // Die Kennung bündelt sie zu einem Rückgängig-Schritt; sie nennt den Block mit, damit
  // die Folge endet, sobald ein anderer Block bearbeitet wird.
  const stylePatch = (extra) => onStylePatch?.({
    block_styles: { [selectedBlock]: { ...bs, ...extra } },
    folge: `stil:${selectedBlock}`,
  });

  const execFmt = (e, cmd) => {
    e.preventDefault();
    if (!isTextBlock) return;

    const blockEl = document.querySelector(`.an-paginas-container [data-block-key="${selectedBlock}"]`);
    const focused = document.activeElement;
    const focusedEditable = (focused?.contentEditable === 'true' && blockEl?.contains(focused)) ? focused : null;
    const editables = blockEl ? [...blockEl.querySelectorAll('[contenteditable]')] : [];
    if (!editables.length) return;

    // If text is selected inside a specific editable, format only that selection
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && focusedEditable && focusedEditable.contains(sel.anchorNode)) {
      document.execCommand(cmd);
      return;
    }

    // No selection: apply to every editable in the block (e.g. both Empfänger fields)
    for (const editable of editables) {
      editable.focus();
      const range = document.createRange();
      range.selectNodeContents(editable);
      const s = window.getSelection();
      if (s) { s.removeAllRanges(); s.addRange(range); }
      document.execCommand(cmd);
    }
    // Collapse into first child so selectionchange reads the element's fontWeight
    const firstEl = editables[0];
    firstEl.focus();
    const firstChild = firstEl.firstElementChild || firstEl.firstChild;
    if (firstChild) {
      const cr = document.createRange();
      cr.setStart(firstChild, 0);
      cr.collapse(true);
      const s = window.getSelection();
      if (s) { s.removeAllRanges(); s.addRange(cr); }
    }
    // Force fmtState refresh — selectionchange may not fire reliably after execCommand
    requestAnimationFrame(() => document.dispatchEvent(new Event('selectionchange')));
  };

  return (
    <div className="an-fixed-toolbar">
      {/* Neues Textfeld — braucht keinen ausgewählten Block und ist deshalb nie gesperrt. */}
      <button
        className="an-block-toolbar-btn"
        title="Textfeld hinzufügen"
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => onTextfeld?.()}
      ><TextfeldNeuIcon /></button>

      <div className="an-block-toolbar-divider" />

      <button
        className={`an-block-toolbar-btn${stempelAktiv ? ' is-active' : ''}`}
        title={stempelAktiv
          ? 'Formatierung aufgenommen — jetzt den Zielblock anklicken'
          : 'Formatierung übertragen'}
        disabled={!isTextBlock && !stempelAktiv}
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => onStempel?.()}
      ><StempelIcon size={15} /></button>

      <div className="an-block-toolbar-divider" />

      {/* Schriftart */}
      <AnFontPicker
        value={bs.fontFamily}
        disabled={!isTextBlock}
        onVorschau={wert => onFontVorschau?.(wert)}
        onWahl={wert => isTextBlock && stylePatch({ fontFamily: wert || undefined })}
      />

      <div className="an-block-toolbar-divider" />

      {/* Ausrichtung */}
      <button
        className={`an-block-toolbar-btn${isTextBlock && currentAlign === 'left' ? ' is-active' : ''}`}
        title="Linksbündig" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => isTextBlock && stylePatch({ textAlign: 'left' })}
      ><AlignLeftIcon size={15} /></button>
      <button
        className={`an-block-toolbar-btn${isTextBlock && currentAlign === 'center' ? ' is-active' : ''}`}
        title="Zentriert" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => isTextBlock && stylePatch({ textAlign: 'center' })}
      ><AlignCenterIcon size={15} /></button>
      <button
        className={`an-block-toolbar-btn${isTextBlock && currentAlign === 'right' ? ' is-active' : ''}`}
        title="Rechtsbündig" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => isTextBlock && stylePatch({ textAlign: 'right' })}
      ><AlignRightIcon size={15} /></button>
      <button
        className={`an-block-toolbar-btn${isTextBlock && currentAlign === 'justify' ? ' is-active' : ''}`}
        title="Blocksatz" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => isTextBlock && stylePatch({ textAlign: 'justify' })}
      ><AlignJustifyIcon size={15} /></button>

      <div className="an-block-toolbar-divider" />

      {/* Schriftgröße */}
      <button
        className="an-block-toolbar-btn"
        title="Schrift kleiner" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => isTextBlock && stylePatch({ fontSize: Math.max(MIN_FONT, aktuelleGroesse - 1) })}
      ><span style={{ fontSize: 16, fontWeight: 400 }}>−</span></button>
      <span className="an-block-toolbar-size-display">
        {isTextBlock ? aktuelleGroesse : '—'}
      </span>
      <button
        className="an-block-toolbar-btn"
        title="Schrift größer" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); e.preventDefault(); }}
        onClick={() => isTextBlock && stylePatch({ fontSize: Math.min(MAX_FONT, aktuelleGroesse + 1) })}
      ><span style={{ fontSize: 16, fontWeight: 400 }}>+</span></button>

      <div className="an-block-toolbar-divider" />

      {/* Fett / Kursiv / Unterstrichen */}
      <button
        className={`an-block-toolbar-btn an-block-toolbar-btn--fmt${isTextBlock && fmtState?.bold ? ' is-active' : ''}`}
        title="Fett" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); execFmt(e, 'bold'); }}
      ><strong style={{ fontSize: 15, lineHeight: 1 }}>B</strong></button>
      <button
        className={`an-block-toolbar-btn an-block-toolbar-btn--fmt${isTextBlock && fmtState?.italic ? ' is-active' : ''}`}
        title="Kursiv" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); execFmt(e, 'italic'); }}
      ><em style={{ fontSize: 15, lineHeight: 1, fontStyle: 'italic' }}>I</em></button>
      <button
        className={`an-block-toolbar-btn an-block-toolbar-btn--fmt${isTextBlock && fmtState?.underline ? ' is-active' : ''}`}
        title="Unterstrichen" disabled={!isTextBlock}
        onPointerDown={e => { e.stopPropagation(); execFmt(e, 'underline'); }}
      ><span style={{ fontSize: 15, textDecoration: 'underline', lineHeight: 1 }}>U</span></button>

      <div className="an-block-toolbar-divider" />

      {/* Textfarbe */}
      <AnColorPicker
        value={bs.color}
        disabled={!isTextBlock}
        onWahl={wert => isTextBlock && stylePatch({ color: wert || undefined })}
      />
    </div>
  );
}

// We measure the actual pixel height at runtime to avoid rounding discrepancies.
const AN_PAGE_CONTENT_H_PX_FALLBACK = 972;

// Höchstzahl der Blätter. Ein Anschreiben, das länger ist, liest ohnehin niemand — und
// die Grenze verhindert, dass ein gehaltener Klick zwanzig leere Seiten anlegt.
const AN_MAX_SEITEN = 3;

// Grenzen für die Verkleinerung des Blattes.
// Nach oben sind es 100 %: Vorher standen hier 90 %, und das hieß, dass der Brief selbst
// auf einem großen Monitor nie in voller Größe zu sehen war — verschenkter Platz ohne
// Grund. Über 100 % geht es bewusst nicht hinaus; darüber wäre die Schrift größer als
// auf dem gedruckten Papier, und die Vorschau würde lügen.
// Nach unten ist bei 45 % Schluss: Darunter kann man den Brief nicht mehr lesen, und ein
// unleserliches Blatt, das ganz aufs Bild passt, hilft niemandem. Ist der Bildschirm so
// flach, darf wieder gescrollt werden.
const AN_SKALA_MAX = 1;
const AN_SKALA_MIN = 0.45;
// Ein Fingerbreit Luft unter dem Blatt, damit es nicht an der Unterkante klebt.
const AN_SKALA_LUFT = 8;

// Startmaße eines frisch angelegten Textfeldes: Breite als Anteil der Textbreite,
// Mindesthöhe in Pixeln. Zusammen ergeben sie ein liegendes Rechteck — vorher bekam
// jedes neue Feld die volle Breite und wirkte dadurch wie ein flacher Strich.
const AN_NEUES_FELD_BREITE = 0.4;
const AN_NEUES_FELD_HOEHE = 90;

const AnschreibenVorschau = forwardRef(function AnschreibenVorschau(
  { stelle, onEditToggle, onInlineChange, onLayoutChange, onAbsenderToggle, onEmpfaengerToggle, onDatumToggle, onAbsenderInlineChange, onEmpfaengerInlineChange, onDatumInlineChange, onUndo, onRedo, canUndo, canRedo, undoCount = 0, redoCount = 0, leftPanel, tabsElement, loading, bearbeitenButton, onBlockSelect, zoom = null, onFit }, ref
) {
  const [profil, setProfil] = useState(null);
  const [nPages, setNPages] = useState(1);
  // Auf welchem Blatt arbeitet der User gerade? Wird beim Antippen einer Seite gesetzt
  // und entscheidet, wo ein neues Textfeld entsteht.
  const [aktiveSeite, setAktiveSeite] = useState(0);
  // Das aufgezogene Auswahlrechteck, solange die Maus gedrückt ist.
  const [rahmen, setRahmen] = useState(null);
  // Merkt sich, dass gerade ein Rahmen gezogen wurde. Ohne das nähme der Klick, der auf
  // das Loslassen folgt, die frische Auswahl sofort wieder zurück.
  const rahmenGezogenRef = useRef(false);
  const [pageH, setPageH] = useState(AN_PAGE_CONTENT_H_PX_FALLBACK);
  // Bis das Lineal gemessen ist, steht in `pageH` nur ein Schätzwert. Auf Seiten
  // verteilt wird erst danach: Die Umrechnung schreibt in echte Bewerbungen, und ein
  // um ein paar Pixel danebenliegender Schnitt bliebe für immer drin.
  const [pageHGemessen, setPageHGemessen] = useState(false);
  // Breite der beschreibbaren Fläche, aus der Messung übernommen.
  const [contentW, setContentW] = useState(0);
  // Blattkante und Sicherheitsrand im Koordinatensystem der Blöcke. Beide sind
  // Zustand und behalten ihre Identität zwischen zwei Neuzeichnungen — daran hängt,
  // dass die Warnung während des Ziehens stehen bleibt (siehe `pruefeLage`).
  const [blatt, setBlatt] = useState(null);
  const [sicher, setSicher] = useState(null);
  // Maße für das Ziehen über eine Seitengrenze.
  const [seitenMass, setSeitenMass] = useState(null);
  // Welche Seite hebt gerade einen gezogenen Block, und auf welcher landet er?
  const [zug, setZug] = useState(null);
  // Welche Blöcke gerade überstehen. Der Streifen wird rot, sobald einer dabei ist —
  // die Namen selbst braucht niemand, aber ohne sie könnten zwei Blöcke einander die
  // Warnung wegnehmen.
  const randVerletzerRef = useRef(new Set());
  const [randVerletzt, setRandVerletzt] = useState(false);
  // Und getrennt davon: Wer endet unter der Blattkante? Nur das rechtfertigt den
  // Hinweis „passt nicht mehr auf die Seite" — ein seitlich abgelegter Block nicht.
  const ueberlaufRef = useRef(new Set());
  // Hier steht kein Ja/Nein, sondern die Namen der betroffenen Blöcke. Vorher hieß die
  // Meldung „Etwas endet unterhalb des Blattes" — und dieses „Etwas" war nicht zu finden,
  // erst recht nicht, wenn der Hinweis gar nicht stimmte. Ein Name macht ihn überprüfbar.
  // Hier stehen die Schlüssel der betroffenen Blöcke, nicht ihre Namen. Der Name allein
  // half nicht weiter: „Textfeld" ist der Ersatzname für jedes selbst angelegte Feld, und
  // liegt so ein Feld unterhalb des Blattes, ist es auf dem Bildschirm nicht zu sehen.
  // Mit dem Schlüssel lässt sich die Meldung anklicken und führt zum Block hin.
  const [ueberlaufKeys, setUeberlaufKeys] = useState([]);

  // Eine Menge führen und nur dann neu zeichnen, wenn sie zwischen leer und nicht-leer
  // wechselt. Ohne die Menge könnten zwei Blöcke einander die Meldung wegnehmen.
  const setzeFlagge = (menge, setzen, blockKey, an) => {
    const vorher = menge.size > 0;
    if (an) menge.add(blockKey); else menge.delete(blockKey);
    const nachher = menge.size > 0;
    if (nachher !== vorher) setzen(nachher);
  };

  // Wie `setzeFlagge`, nur dass nach oben die Namen gehen statt eines Ja/Nein. Die Menge
  // bleibt trotzdem nötig: Ohne sie könnten zwei Blöcke einander die Meldung wegnehmen.
  const setzeUeberlauf = (blockKey, an) => {
    const menge = ueberlaufRef.current;
    if (an) menge.add(blockKey); else menge.delete(blockKey);
    const keys = [...menge];
    setUeberlaufKeys(vorher => (vorher.join('|') === keys.join('|') ? vorher : keys));
  };

  const meldeRandVerletzung = useCallback((blockKey, lage) => {
    setzeFlagge(randVerletzerRef.current, setRandVerletzt, blockKey, lage?.rand);
    setzeUeberlauf(blockKey, lage?.unten);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kommt bei jeder Zeigerbewegung; neu gezeichnet wird nur, wenn sich wirklich etwas
  // ändert — sonst rechnete der ganze Brief bei jedem Pixel neu.
  const meldeZug = useCallback((neu) => {
    setZug(alt => (alt?.von === neu?.von && alt?.ziel === neu?.ziel ? alt : neu));
  }, []);
  const [activeEditKey, setActiveEditKey] = useState(null);
  const [blockOffsets, setBlockOffsets] = useState(() => stelle?.an_layout?.offsets || {});
  // Was von oben kommt, gilt. Früher stand hier zusätzlich ein Merker über den zuletzt
  // nach oben gemeldeten Stand, und alles, was diesem Stand glich, wurde ignoriert.
  // Genau das machte Wiederholen wirkungslos: Es stellt ja den Stand wieder her, den
  // dieser Block zuletzt selbst gemeldet hat. Der Vergleich mit dem eigenen Zustand
  // eine Zeile tiefer verhindert die Endlosschleife auch ohne den Merker.
  useLayoutEffect(() => {
    const next = stelle?.an_layout?.offsets || {};
    setBlockOffsets(prev => JSON.stringify(prev) === JSON.stringify(next) ? prev : next);
  }, [stelle?.an_layout?.offsets]);
  const inDragMode = !!onLayoutChange;

  // Außerhalb des Bearbeitungsmodus meldet kein Block mehr seine Lage — die Meldefunktion
  // wird dort bewusst nicht durchgereicht. Was in den Mengen übrig bleibt, kann deshalb
  // niemand mehr richtigstellen, und eine überholte Warnung stünde bis zum Neuladen da.
  // Also beim Verlassen leeren. Das verliert nichts: Beim nächsten Einschalten meldet
  // jeder Block seine Lage von selbst neu, sobald die Papierfläche wieder feststeht.
  useEffect(() => {
    if (inDragMode) return;
    randVerletzerRef.current.clear();
    ueberlaufRef.current.clear();
    setRandVerletzt(false);
    setUeberlaufKeys([]);
  }, [inDragMode]);

  // Ausgewählt ist eine Liste, keine einzelne Sache. Werkzeugleiste, Anfasser und
  // Formatierung gelten aber nur für genau einen Block — deshalb dieser abgeleitete
  // Wert, an dem alles Bestehende unverändert weiterhängt.
  const [auswahl, setAuswahl] = useState([]);
  const selectedBlock = auswahl.length === 1 ? auswahl[0] : null;
  const [fmtState, setFmtState] = useState({ bold: false, italic: false, underline: false, fontSize: null });
  // Schrift, die gerade nur zur Ansicht gezeigt wird — null heißt: nichts überfahren.
  const [fontVorschau, setFontVorschau] = useState(null);
  useEffect(() => { setFontVorschau(null); }, [selectedBlock, inDragMode]);
  // Aufgenommene Formatierung des Stempels — null heißt: kein Stempel geladen.
  const [stempel, setStempel] = useState(null);
  useEffect(() => { if (!inDragMode) setStempel(null); }, [inDragMode]);
  useEffect(() => {
    if (!inDragMode || !selectedBlock) return;
    // text-decoration is NOT inherited in CSS — walk ancestors to detect underline reliably
    const hasUnderline = el => {
      let c = el;
      while (c && c.contentEditable !== 'true') {
        if ((window.getComputedStyle(c).textDecorationLine ?? '').includes('underline')) return true;
        c = c.parentElement;
      }
      return false;
    };
    const istFett   = el => parseInt(window.getComputedStyle(el).fontWeight, 10) >= 700;
    const istKursiv = el => window.getComputedStyle(el).fontStyle === 'italic';
    const groesse   = el => Math.round(parseFloat(window.getComputedStyle(el).fontSize));

    // Der Zustand beschreibt die Markierung, nicht das erste Zeichen des Blocks.
    const update = () => {
      const sel = window.getSelection();
      if (!sel?.rangeCount) return;
      const range = sel.getRangeAt(0);

      let elemente;
      if (sel.isCollapsed) {
        const node = sel.focusNode;
        let el = node ? (node.nodeType === Node.TEXT_NODE ? node.parentElement : node) : null;
        // Cursor AT an element boundary (e.g. after execCommand collapse) — walk to innermost text node for accurate CSS
        if (el?.firstElementChild) {
          const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
          const txt = w.nextNode();
          if (txt) el = txt.parentElement;
        }
        elemente = el ? [el] : [];
      } else {
        elemente = textknotenImBereich(range).map(n => n.parentElement).filter(Boolean);
      }

      const b = einheitlicherWert(elemente, istFett) === true;
      const i = einheitlicherWert(elemente, istKursiv) === true;
      const u = einheitlicherWert(elemente, hasUnderline) === true;
      const size = einheitlicherWert(elemente, groesse);
      setFmtState(prev => (prev.bold === b && prev.italic === i && prev.underline === u && prev.fontSize === size) ? prev : { bold: b, italic: i, underline: u, fontSize: size });
    };
    document.addEventListener('selectionchange', update);
    // First-click: cursor not yet in contentEditable, selectionchange won't fire — read block element directly
    const blockEl = document.querySelector(`[data-block-key="${selectedBlock}"] [contenteditable]`);
    if (blockEl) {
      // Prefer the actual cursor position; fall back to firstElementChild if cursor is outside the block
      const selNow = window.getSelection();
      const selNodeNow = selNow?.focusNode;
      const selElNow = selNodeNow
        ? (selNodeNow.nodeType === Node.TEXT_NODE ? selNodeNow.parentElement : selNodeNow)
        : null;
      // Parent of first text node = innermost element, which inherits all CSS (bold/italic) from ancestors
      const firstTextParent = () => {
        const w = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
        return w.nextNode()?.parentElement || blockEl.firstElementChild || blockEl;
      };
      const styleEl = (selElNow && blockEl.contains(selElNow)) ? selElNow : firstTextParent();
      const bcs = window.getComputedStyle(styleEl);
      const blockBold = parseInt(bcs.fontWeight, 10) >= 700;
      const blockItalic = bcs.fontStyle === 'italic';
      const blockUnderline = hasUnderline(styleEl);
      const blockFontSize = Math.round(parseFloat(bcs.fontSize));
      setFmtState(prev =>
        (prev.bold === blockBold && prev.italic === blockItalic && prev.underline === blockUnderline && prev.fontSize === blockFontSize)
          ? prev
          : { bold: blockBold, italic: blockItalic, underline: blockUnderline, fontSize: blockFontSize }
      );
    } else {
      setFmtState({ bold: false, italic: false, underline: false, fontSize: null });
    }
    return () => document.removeEventListener('selectionchange', update);
  }, [inDragMode, selectedBlock]);
  const measureRef = useRef(null);
  const rulerRef = useRef(null);
  const ersteSeiteRef = useRef(null);
  const aussenRef = useRef(null);

  // Einzige Quelle für Blockpositionen: der verborgene Klon rendert immer im
  // natürlichen Fluss und ist exakt so breit wie der echte Textbereich.
  // Ausgelöst wird die Messung nicht mehr vom Bearbeitungsmodus, sondern von der
  // Sichtbarkeit: bei Breite 0 (verborgener Tab) passiert nichts, der ResizeObserver
  // holt die Messung nach, sobald der Brief sichtbar wird.
  const messenUndSetzen = useCallback(() => {
    const hc = measureRef.current;
    const root = hc?.querySelector('.anschreiben');
    if (!root) return;
    const cr = root.getBoundingClientRect();
    if (!cr.width) return;
    const rects = {};
    for (const el of hc.querySelectorAll('[data-block-key]')) {
      const r = el.getBoundingClientRect();
      if (!r.width) continue;
      rects[el.dataset.blockKey] = {
        top: r.top - cr.top,
        left: r.left - cr.left,
        width: r.width,
      };
    }
    // Der Klon misst einen durchgehenden Fluss und weiß nichts von Seiten. Die
    // Startpositionen werden deshalb hier auf Seiten verteilt, bevor sie in die
    // gespeicherten Werte einfließen — sonst käme bei einem langen Brief die
    // Unterschrift mit einem `top` von 1100 auf Seite 0 an und wäre dort nicht zu sehen.
    const gemessen = pageHGemessen
      ? aufSeitenVerteilen(startLayoutAusRects(rects, cr.width), pageH).offsets
      : startLayoutAusRects(rects, cr.width);
    setBlockOffsets(prev => mergeLayout(prev, gemessen));
    // Dieselbe Messung liefert die Breite der beschreibbaren Fläche. Sie zu merken
    // spart dem Parkbereich später eine eigene Messung.
    setContentW(prev => (prev === cr.width ? prev : cr.width));
  }, [pageH, pageHGemessen]);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    messenUndSetzen();
    const obs = new ResizeObserver(() => messenUndSetzen());
    obs.observe(el);
    return () => obs.disconnect();
    // an_rohtext in den Abhängigkeiten: nach einer Neugenerierung sind die Positionen
    // geleert und müssen neu gemessen werden, auch wenn der Klon zufällig gleich hoch ist.
  }, [messenUndSetzen, stelle?.id, stelle?.an_rohtext]);

  // Wo liegen Blattkante und Sicherheitsrand, gemessen im Koordinatensystem der Blöcke?
  // Die Maße stehen im Stylesheet und werden dort abgelesen statt hier ein zweites Mal
  // aufgeschrieben — sonst liefe die Warnung irgendwann neben der gezeigten Linie.
  useLayoutEffect(() => {
    const seite = ersteSeiteRef.current?.closest('.an-pagina');
    if (!seite || !contentW) { setBlatt(null); setSicher(null); return; }
    const cs = getComputedStyle(seite);
    // DIN A4 ist definitionsgemäß 210 mm breit — daraus folgt der Maßstab.
    const pxProMm = seite.offsetWidth / 210;
    const rand = (parseFloat(cs.getPropertyValue('--an-sicherheitsrand')) || 0) * pxProMm;

    // Die Ränder der Seite führen vom Textbereich zur Blattkante. Nach unten zählt
    // die letzte Seite: Jede Seite ist im Koordinatensystem genau `pageH` hoch.
    const links  = -parseFloat(cs.paddingLeft);
    const oben   = -parseFloat(cs.paddingTop);
    const rechts = contentW + parseFloat(cs.paddingRight);
    // Seit die Koordinaten seitenrelativ sind, gilt die untere Kante pro Blatt — nicht
    // mehr für den ganzen Stapel.
    const unten  = pageH + parseFloat(cs.paddingBottom);

    // Diese Kante ist die Blattkante selbst, OHNE den Sicherheitsrand. Vorher wurde er
    // abgezogen, und damit log die Meldung: Sie behauptet „endet unterhalb des Blattes,
    // gedruckt wird nur, was auf dem Papier steht" — ausgelöst wurde sie aber schon von
    // einem Block, der noch vollständig auf dem Papier lag und ganz normal mitgedruckt
    // worden wäre, nur eben im unteren Rand. Dass er im Rand steht, zeigt bereits der rot
    // leuchtende Streifen; dafür braucht es keine zweite, falsche Meldung.

    // Nach unten gilt für das Ausgrauen KEINE Grenze — und das ist kein Schlendrian,
    // sondern verhindert, dass ein Brief sich selbst auffrisst:
    //
    // Ein ausgegrauter Block wird in der reinen Anzeige ausgeblendet. Ausgeblendet
    // zählt er bei der Messung nicht mehr mit, dadurch sinkt die Seitenzahl, dadurch
    // rückt die untere Blattkante nach oben — und der nächste Block liegt plötzlich
    // auch „unter dem Blatt" und verschwindet. Ein Block nach dem anderen.
    //
    // Der Denkfehler steckt in der Frage selbst: Nach unten wächst das Papier mit dem
    // Inhalt. „Unter dem Blatt" gibt es gar nicht, es gibt nur „auf der nächsten
    // Seite". Wirklich ablegen kann man einen Block nur seitlich oder nach oben.
    // Die Maße, mit denen beim Ziehen über eine Seitengrenze gerechnet wird. Sie stehen
    // im Stylesheet und werden dort abgelesen — dieselbe Regel wie beim Sicherheitsrand.
    setSeitenMass({
      pageH,
      randOben:  parseFloat(cs.paddingTop),
      randUnten: parseFloat(cs.paddingBottom),
      luecke:    (parseFloat(cs.getPropertyValue('--an-blatt-luecke')) || 0),
    });

    setBlatt({ links, oben, rechts, unten: Infinity });
    // Die Warnung darf die untere Kante ruhig kennen: Sie färbt nur und löscht nichts.
    // `papierUnten` steht daneben und ist bewusst eine andere Zahl: Der Sicherheitsrand
    // entscheidet, ob der Streifen rot wird — die Blattkante entscheidet, ob etwas noch
    // gedruckt wird. Zwei Fragen, zwei Grenzen.
    setSicher({
      links: links + rand, oben: oben + rand, rechts: rechts - rand, unten: unten - rand,
      papierUnten: unten,
    });
  }, [contentW, pageH, inDragMode]);

  useEffect(() => {
    if (!stelle?.profil_name) return;
    getProfil(stelle.profil_name)
      .then((r) => setProfil(r.data))
      .catch(() => {});
  }, [stelle?.profil_name]);

  // Measure actual pixel height of 257mm once on mount
  useEffect(() => {
    if (rulerRef.current) {
      const h = rulerRef.current.clientHeight;
      if (h > 0) { setPageH(h); setPageHGemessen(true); }
    }
  }, []);

  // Ein ganzes Blatt soll immer ins Bild passen, ohne Scrollen — auf jedem Monitor.
  //
  // Im Stylesheet stand dafür die feste Zahl 0,9. Die passte auf genau einem Bildschirm:
  // Ein Blatt ist 297 mm hoch, das sind rund 1120 Punkte, und darüber sitzen noch
  // Kopfzeile, Reiter und Werkzeugleiste. Auf einem flacheren Monitor lag deshalb die
  // untere Hälfte außerhalb des Fensters — und ausgerechnet dort steht der Hinweis, dass
  // etwas nicht mehr aufs Papier passt. Der Fehler war also nicht zu sehen, nur seine
  // Meldung.
  //
  // Verkleinert wird weiterhin der Kasten und nicht das Blatt. Das ist wichtig: Alle
  // Messungen INNEN (Blattbreite, Ränder, Blockpositionen) bleiben dadurch unverändert,
  // und `blattSkala()` liest den Faktor ohnehin am Blatt selbst ab. Hier ändert sich nur
  // die eine Zahl, mit der verkleinert wird.
  useLayoutEffect(() => {
    const aussen = aussenRef.current;
    if (!aussen) return;
    // Gescrollt wird die Dokument-Insel weiter außen, nicht dieser Kasten.
    const roller = aussen.closest('.se-main-scroll');

    const anpassen = () => {
      const blatt = aussen.querySelector('.an-pagina');
      const kasten = aussen.querySelector('.an-paginas-container');
      if (!blatt?.offsetHeight) return false;

      // Platzbedarf eines Blattes samt Innenabstand — in unverkleinerten Punkten, denn
      // genau darauf wirkt der Faktor.
      const rand = kasten ? parseFloat(getComputedStyle(kasten).paddingTop) || 0 : 0;
      const gebraucht = blatt.offsetHeight + 2 * rand;

      // Bezugspunkt ist die Oberkante des Ergebnis-Blocks, nicht mehr der Anfang des
      // scrollbaren Inhalts. Das ist der Kern der Änderung vom 2026-08-11: Der Block wird
      // beim Anzeigen an die Oberkante der Insel geschoben (siehe StellenEditor.js), und
      // die Leisten darüber wandern aus dem Bild. Rechnete man ihre Höhe weiter vom
      // verfügbaren Platz ab, bliebe das Blatt klein, obwohl der Platz längst frei ist —
      // genau das war die Beschwerde. Beide Kanten bewegen sich beim Scrollen gemeinsam,
      // die Zahl bleibt also stabil und das Blatt zappelt nicht.
      const ergebnis = aussen.closest('.se-ergebnis');
      const bezugOben = ergebnis
        ? ergebnis.getBoundingClientRect().top
        : (roller ? roller.getBoundingClientRect().top - roller.scrollTop : -window.scrollY);
      const obenDrueber = aussen.getBoundingClientRect().top - bezugOben;

      const rollerStil = roller ? getComputedStyle(roller) : null;
      const sichtbar = roller
        ? roller.clientHeight
          - (parseFloat(rollerStil.paddingTop)    || 0)
          - (parseFloat(rollerStil.paddingBottom) || 0)
        : window.innerHeight;

      // Unter dem Blatt kommt noch der untere Innenabstand der Vorschau. Ohne ihn bliebe
      // genau dieser Streifen übrig — und ein Streifen genügt, damit der Balken zum
      // Scrollen wieder auftaucht. (Die Abstände der Insel stecken schon in `sichtbar`.)
      const wrapper = aussen.closest('.an-vorschau-wrapper');
      const untenLuft = AN_SKALA_LUFT
        + (wrapper ? parseFloat(getComputedStyle(wrapper).paddingBottom) || 0 : 0);

      const frei = sichtbar - obenDrueber - untenLuft;

      // Die Breite begrenzt jetzt mit. Solange die Obergrenze bei 90 % lag, konnte das
      // Blatt seitlich gar nicht anstoßen; bei 100 % schon. Ohne diese Bremse würde ein
      // schmales Fenster das Blatt rechts abschneiden.
      const randX = kasten ? parseFloat(getComputedStyle(kasten).paddingLeft) || 0 : 0;
      const breitenFit = blatt.offsetWidth
        ? (aussen.clientWidth - 2 * randX) / blatt.offsetWidth
        : AN_SKALA_MAX;

      const passend = Math.min(frei / gebraucht, breitenFit);
      const fit = Math.min(AN_SKALA_MAX, Math.max(AN_SKALA_MIN, passend));
      // Der eingepasste Wert wird immer gemeldet, auch während der User zoomt: Der Knopf
      // „Anpassen" im Zoom-Regler muss wissen, wohin er zurückstellt.
      onFit?.(fit);

      // Hat der User selbst gezoomt, gilt seine Zahl. Die Einpassung rechnet weiter mit,
      // überschreibt sie aber nicht mehr — sonst spränge das Blatt bei jeder
      // Fenster-Änderung aus seiner Größe zurück.
      const skala = zoom ?? fit;
      const gerundet = String(Math.round(skala * 1000) / 1000);
      // Nur schreiben, wenn sich wirklich etwas ändert — ein Stil-Schreibvorgang bei
      // jedem Messen würde sonst reihenweise überflüssige Neuberechnungen auslösen.
      if (aussen.style.getPropertyValue('--an-blatt-skala') !== gerundet) {
        aussen.style.setProperty('--an-blatt-skala', gerundet);
      }
      return true;
    };

    // Beobachtet wird AUSSCHLIESSLICH die scrollende Insel — niemals der Kasten mit den
    // Blättern und nichts darin.
    //
    // Genau daran ist der erste Anlauf gescheitert: Der Kasten stand mit in der
    // Beobachtung, und `anpassen` verändert über den Maßstab seine Höhe. Der Beobachter
    // meldete also seine eigene Wirkung zurück, Runde um Runde, bis der Browser abbrach
    // („ResizeObserver loop completed with undelivered notifications"). Dass die zweite
    // Messung dieselbe Zahl ergibt, half nicht: Gemeldet wird die Größenänderung, nicht
    // das Ergebnis der Rechnung.
    //
    // Die Insel ist dagegen sicher. Ihre Höhe kommt vom Fenster, nicht vom Inhalt — was
    // in ihr wächst oder schrumpft, lässt sie unberührt.
    const obs = new ResizeObserver(anpassen);
    obs.observe(roller || document.documentElement);

    // Und zusätzlich: neu rechnen, sobald das Anschreiben überhaupt sichtbar wird.
    //
    // Ohne das blieb der Brief in voller Größe stehen und passte erst nach einem Wechsel
    // in den Bearbeitungsmodus ins Bild. Der Grund: Beim Öffnen steht der Lebenslauf
    // vorn, das Anschreiben liegt daneben auf „unsichtbar". Ein unsichtbares Blatt ist
    // null Punkte hoch, die Messung scheitert, und der eine Nachfass-Versuch eine Zeile
    // weiter unten scheitert genauso — er kommt ja im selben Atemzug. Danach fragt
    // niemand mehr nach: Der Beobachter oben wacht über die Insel, und die ändert ihre
    // Größe beim Umschalten der Reiter nicht. Also blieb der Startwert stehen.
    // Erst der Bearbeitungsmodus setzte diesen Effekt neu auf — dann war das Blatt
    // sichtbar und die Rechnung ging auf. Genau das hat der User am 2026-08-11 gesehen.
    //
    // Der Sichtbarkeits-Beobachter meldet den Übergang von „nicht da" zu „da" und ist
    // dabei ungefährlich: Anders als ein Größen-Beobachter am Kasten selbst schlägt er
    // nicht an, wenn die Rechnung die Höhe verändert — sonst meldete er seine eigene
    // Wirkung zurück, Runde um Runde.
    const sicht = new IntersectionObserver((eintraege) => {
      if (eintraege.some(e => e.isIntersecting)) anpassen();
    });
    sicht.observe(aussen);

    // Falls das Blatt im ersten Durchgang noch nicht steht, wird genau einmal im
    // nächsten Bild nachgefasst. Eine Schleife ist das nicht — es bleibt bei dem einen
    // Versuch, und danach halten die Beobachter alles Weitere aktuell.
    let nachfassen = 0;
    if (!anpassen()) nachfassen = requestAnimationFrame(anpassen);

    return () => { cancelAnimationFrame(nachfassen); obs.disconnect(); sicht.disconnect(); };
    // `inDragMode` steht in der Liste, weil die Werkzeugleiste über dem Blatt erscheint
    // und verschwindet — sie verschiebt den Kasten, ohne die Insel zu verändern.
  }, [inDragMode, loading, zoom, onFit]);

  // Briefe, die vor der Umstellung gespeichert wurden, haben durchgehende Koordinaten
  // ohne Seitenangabe. Sie werden einmal umgerechnet, sobald die Seitenhöhe bekannt ist.
  // Danach sehen sie aus wie vorher — nur dass jeder Block jetzt weiß, auf welchem Blatt
  // er steht. Die Rechnung ist wiederholbar, ein zweiter Durchlauf ändert nichts mehr.
  useEffect(() => {
    if (!pageHGemessen || !pageH || !blockOffsets) return;
    const offen = Object.values(blockOffsets)
      .some(o => o && typeof o === 'object' && o.top != null && o.seite == null);
    if (!offen) return;
    const { offsets } = aufSeitenVerteilen(blockOffsets, pageH);
    setBlockOffsets(offsets);
    // Nur im Bearbeitungsmodus gibt es einen Weg zum Speichern. In der reinen Anzeige
    // wirkt die Umrechnung trotzdem — sie wird dann beim nächsten Bearbeiten gesichert.
    onLayoutChange?.({ offsets, ohneRueckgaengig: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageHGemessen, pageH, blockOffsets]);

  // Ein frisch erzeugter Brief hat oben noch gar keine Positionen: Seine Blöcke stehen
  // dort, wo die Messung sie hingelegt hat, und das weiß bis zur ersten Bearbeitung nur
  // dieser Block hier. Rückgängig hätte dann nichts, wohin es zurückkönnte — der erste
  // Klick darauf nähme nicht die Änderung des Users zurück, sondern das ganze Layout,
  // und der Brief spränge in den rohen Fluss. Deshalb werden die Startpositionen einmal
  // nach oben gemeldet, sobald sie feststehen. Das ist keine Bearbeitung des Users und
  // bekommt darum keinen eigenen Schritt.
  const hatGespeichertePositionen = Object.keys(stelle?.an_layout?.offsets || {}).length > 0;
  useEffect(() => {
    if (!pageHGemessen || hatGespeichertePositionen) return;
    if (!Object.keys(blockOffsets || {}).length) return;
    onLayoutChange?.({ offsets: blockOffsets, ohneRueckgaengig: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageHGemessen, hatGespeichertePositionen, blockOffsets]);

  // Die Seitenzahl wird nicht aus der Inhaltshöhe erraten — damit keine Seite von selbst
  // entsteht, während der User tippt, und keine verschwindet, weil eine Messung gerade
  // etwas anderes ergab.
  //
  // Sie steht als eigene Zahl im Layout und gehört damit dem User, nicht dem Inhalt.
  // Vorher folgte sie allein dem höchsten Seitenzeiger der Blöcke — dann starb eine
  // frisch angelegte Seite in dem Moment, in dem man ihr einziges Textfeld löschte.
  // Der höchste Zeiger zählt weiterhin mit, aber nur als Untergrenze: Kein Block darf
  // je hinter dem letzten Blatt liegen, auch nicht in einem alten Brief, der die Zahl
  // noch gar nicht kennt.
  const gespeicherteSeiten = stelle?.an_layout?.seiten;
  useEffect(() => {
    setNPages(Math.max(1, gespeicherteSeiten || 1, ...Object.values(blockOffsets || {})
      .filter(o => o && typeof o === 'object' && o.seite != null)
      .map(o => o.seite + 1)));
  }, [blockOffsets, gespeicherteSeiten]);

  // Ein frisch erzeugter Brief bringt keine Seitenzahl mit: Seine Blöcke werden beim
  // ersten Messen auf Seiten verteilt, aber die Zahl selbst stand nirgends. Solange sie
  // fehlt, lebt die zweite Seite nur davon, dass ein Block auf ihr liegt — und stürbe
  // mit dessen Löschen, genau der Fehler, gegen den die eigene Zahl eingeführt wurde.
  // Deshalb wird sie hier einmal festgeschrieben, sobald der Brief länger als ein Blatt
  // ist. Nur nach oben: Seiten wegnehmen darf allein der User.
  useEffect(() => {
    if (!pageHGemessen || nPages <= (gespeicherteSeiten || 1)) return;
    onLayoutChange?.({ seiten: nPages, ohneRueckgaengig: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageHGemessen, nPages, gespeicherteSeiten]);

  const handleLayoutPatch = (patch) => {
    onLayoutChange?.(patch);
  };

  // `folge` kennzeichnet Änderungen, die zu einer zusammenhängenden Bedienung gehören
  // (Pfeiltasten gedrückt halten). Sie werden oben zu einem Rückgängig-Schritt und
  // einem Speichervorgang gebündelt. Ziehen und Größenändern lassen sie leer: Jeder
  // Zug ist für sich abgeschlossen und verdient seinen eigenen Schritt.
  const handleOffsetChange = (key, value, stilPatch, folge) => {
    const next = { ...blockOffsets, [key]: { ...value, dragged: true } };
    setBlockOffsets(next);
    handleLayoutPatch({
      offsets: next,
      ...(stilPatch ? { block_styles: stilPatch } : {}),
      ...(folge ? { folge } : {}),
    });
  };

  // Tastatursteuerung, soweit sie den ganzen Brief betrifft. Die Pfeiltasten liegen
  // bewusst nicht hier, sondern im Block selbst — sie brauchen dessen Einrast-Rechnung.
  useEffect(() => {
    if (!inDragMode) return;
    const abwaehlen = () => { setStempel(null); setAuswahl([]); onBlockSelect?.(null); };
    const onKey = (e) => {
      if (tastenGehoerenDemText()) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        (e.shiftKey ? onRedo : onUndo)?.();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || !selectedBlock) return;

      if (e.key === 'Escape') { e.preventDefault(); abwaehlen(); return; }

      // Nur Entf, nicht die Rücktaste. Die Rücktaste ist die Taste, die man beim
      // Schreiben tausendmal drückt — landete sie einmal neben dem Text, war der
      // halbe Brief weg. Entf ist dieselbe Geste, aber keine, die man versehentlich
      // trifft. Auf Mac-Tastaturen ohne eigene Entf-Taste: Fn + Rücktaste.
      if (e.key === 'Delete') {
        e.preventDefault();
        // Ein fester Block wird nur ausgeblendet — sein Text gehört zum Brief und soll
        // über Rückgängig zurückkommen. Ein selbst angelegtes Textfeld wird wirklich
        // gelöscht; ein ausgeblendetes leeres Feld ohne Weg zurück wäre nur Ballast.
        if (eigeneFelder.includes(selectedBlock)) {
          const { [selectedBlock]: weg, ...rest } = blockOffsets;
          setBlockOffsets(rest);
          handleLayoutPatch({ offsets: rest, eigene: eigeneFelder.filter(id => id !== selectedBlock) });
        } else {
          handleOffsetChange(selectedBlock, {
            ...(blockOffsets[selectedBlock] || {}),
            versteckt: true,
            spurHoehe: gemesseneHoehe(selectedBlock),
          });
        }
        abwaehlen();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inDragMode, selectedBlock, blockOffsets, onUndo, onRedo, onBlockSelect]);

  if (loading) {
    return (
      <div className="an-vorschau-wrapper">
        <div className="an-vorschau-label">
          {(onUndo || onRedo) && (
            <div className="cv-history-btns">
              <button className="cv-history-btn" disabled title="Rückgängig"><UndoIcon /></button>
              <button className="cv-history-btn" disabled title="Wiederherstellen"><RedoIcon /></button>
            </div>
          )}
          {tabsElement || <span>Anschreiben Vorschau</span>}
        </div>
        <div className="an-paginas-outer">
          <div className="an-paginas-container">
            <div className="an-pagina an-pagina-modern">
              <div className="se-an-skeleton">
                {/* Absenderblock – rechtsbündig */}
                <div className="se-an-skel-line se-an-skel-line--sm se-an-skel-line--right" />
                <div className="se-an-skel-line se-an-skel-line--xs se-an-skel-line--right" style={{marginBottom:24}} />
                {/* Empfängerblock */}
                <div className="se-an-skel-line se-an-skel-line--md" />
                <div className="se-an-skel-line se-an-skel-line--sm" />
                <div className="se-an-skel-line se-an-skel-line--xs" style={{marginBottom:28}} />
                {/* Datum – rechtsbündig */}
                <div className="se-an-skel-line se-an-skel-line--xs se-an-skel-line--right" style={{marginBottom:18}} />
                {/* Betreff */}
                <div className="se-an-skel-line se-an-skel-line--lg" style={{height:18, marginBottom:18}} />
                {/* Anrede */}
                <div className="se-an-skel-line se-an-skel-line--md" style={{marginBottom:14}} />
                {/* Absatz 1 */}
                <div className="se-an-skel-line" />
                <div className="se-an-skel-line" />
                <div className="se-an-skel-line" />
                <div className="se-an-skel-line se-an-skel-line--lg" style={{marginBottom:14}} />
                {/* Absatz 2 */}
                <div className="se-an-skel-line" />
                <div className="se-an-skel-line" />
                <div className="se-an-skel-line se-an-skel-line--md" style={{marginBottom:14}} />
                {/* Absatz 3 */}
                <div className="se-an-skel-line" />
                <div className="se-an-skel-line" />
                <div className="se-an-skel-line se-an-skel-line--sm" style={{marginBottom:32}} />
                {/* Gruß + Unterschrift */}
                <div className="se-an-skel-line se-an-skel-line--sm" style={{marginBottom:24}} />
                <div className="se-an-skel-line se-an-skel-line--xs" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stelle?.an_rohtext) return null;

  const istModern = stelle.cv_data
    ? (stelle.cv_data.sektionen || []).some((s) => s.typ === 'kenntnisse')
    : stelle.cv_rohtext
      ? (stelle.cv_rohtext.includes('KTITEL:') ||
         (stelle.cv_rohtext.includes('FIRMA:') && !stelle.cv_rohtext.includes('JOBTITEL:')))
      : true;
  const template = 'modern';

  const handleEditToggle = onEditToggle
    ? (item) => onEditToggle(item, profil, stelle.an_rohtext)
    : undefined;

  const handleStylePatch = (patch) => {
    handleLayoutPatch(patch);
  };

  const basisBlockStyles = stelle?.an_layout?.block_styles || {};

  // Fett, kursiv und unterstrichen hängen am Text selbst, nicht am Block. Deshalb
  // werden sie über die Textbefehle gesetzt statt über die Blockvorgaben.
  const formatSetzen = (key, fmt) => {
    const blockEl = document.querySelector(`.an-paginas-container [data-block-key="${key}"]`);
    const editables = blockEl ? [...blockEl.querySelectorAll('[contenteditable]')] : [];
    const sel = window.getSelection();
    if (!editables.length || !sel) return;
    for (const ed of editables) {
      ed.focus();
      const r = document.createRange();
      r.selectNodeContents(ed);
      sel.removeAllRanges();
      sel.addRange(r);
      for (const cmd of ['bold', 'italic', 'underline']) {
        if (document.queryCommandState(cmd) !== !!fmt[cmd]) document.execCommand(cmd);
      }
    }
    // Der letzte Block muss den Fokus abgeben, sonst wird sein Text nicht gesichert.
    editables[editables.length - 1].blur();
    sel.removeAllRanges();
  };

  const handleStempel = () => {
    if (stempel) { setStempel(null); return; }
    if (!selectedBlock) return;
    const quelle = basisBlockStyles[selectedBlock] || {};
    setStempel({
      // Was der Nutzer sieht, nicht was gespeichert ist: ohne eigene Vorgabe gelten
      // die gemessene Größe und die Anfangsausrichtung des Blocks.
      styles: {
        textAlign:  quelle.textAlign  || AN_START_AUSRICHTUNG[selectedBlock] || 'left',
        fontFamily: quelle.fontFamily,
        fontSize:   quelle.fontSize ?? fmtState.fontSize ?? undefined,
        color:      quelle.color,
      },
      fmt: { bold: fmtState.bold, italic: fmtState.italic, underline: fmtState.underline },
    });
  };

  // `additiv` ist der Umschalt-Klick: er legt einen Block zur Auswahl dazu oder nimmt
  // ihn wieder heraus, statt alles andere abzuwählen.
  const selectBlock = (key, additiv = false) => {
    if (!key) { setAuswahl([]); onBlockSelect?.(null); return; }

    if (additiv) {
      const neu = auswahl.includes(key) ? auswahl.filter(k => k !== key) : [...auswahl, key];
      setAuswahl(neu);
      onBlockSelect?.(neu.length === 1 ? neu[0] : null);
      return;
    }

    if (stempel && key !== selectedBlock) {
      handleStylePatch({ block_styles: { [key]: { ...(basisBlockStyles[key] || {}), ...stempel.styles } } });
      const fmt = stempel.fmt;
      requestAnimationFrame(() => formatSetzen(key, fmt));
      setStempel(null);
    }
    setAuswahl([key]);
    onBlockSelect?.(key);
  };

  // Einen Block auswählen UND ins Bild holen. Gebraucht für die Überlauf-Meldung: Der
  // Block, über den sie sich beschwert, liegt unterhalb des Blattes und steht damit
  // außerhalb dessen, was man gerade sieht. Auswählen allein hülfe nichts — man sähe die
  // Markierung ja nicht. Beim Bearbeiten schneidet das Blatt nichts ab (`overflow:
  // visible`), der Block ist dort also wirklich zu erreichen.
  const zeigeBlock = (key) => {
    selectBlock(key);
    const el = aussenRef.current?.querySelector(`[data-block-key="${key}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  // ── Auswahlrechteck ──────────────────────────────────────────
  // Auf freier Fläche drücken und ziehen wählt alles aus, was das Rechteck berührt —
  // wie auf dem Schreibtisch des Rechners und wie in Canva.
  //
  // Gerechnet wird durchweg in Bildschirmkoordinaten. Das klingt umständlich, ist aber
  // das Gegenteil: Der Rahmen kann so unverändert angezeigt werden, und die Blöcke
  // liefern ihre Lage über `getBoundingClientRect` im selben System. Es gibt also gar
  // nichts umzurechnen — und damit auch nichts, was beim späteren Verkleinern des
  // Blattes falsch umgerechnet werden könnte.
  // Ab hier gilt es als Ziehen, darunter als Klick. Ohne die Schwelle würde jeder
  // Klick mit einem Pixel Wackeln eine leere Auswahl aufziehen.
  const RAHMEN_SCHWELLE = 4;

  const rahmenStarten = (e, seiteEl) => {
    const sx = e.clientX, sy = e.clientY;
    let gezogen = false;

    // Berühren genügt, vollständiges Umschließen wird nicht verlangt — sonst müsste man
    // den Brieftext immer ganz einrahmen, um ihn zu erwischen.
    const trefferIm = (x, y) => {
      const links = Math.min(sx, x), rechts = Math.max(sx, x);
      const oben  = Math.min(sy, y), unten  = Math.max(sy, y);
      return [...seiteEl.querySelectorAll('.an-drag-block[data-block-key]')]
        .filter(el => {
          const b = el.getBoundingClientRect();
          return b.left < rechts && b.right > links && b.top < unten && b.bottom > oben;
        })
        .map(el => el.dataset.blockKey);
    };

    // Die Auswahl wird schon WÄHREND des Ziehens gesetzt, nicht erst beim Loslassen.
    // Sonst sieht man bis zuletzt nur den Block unter dem Zeiger umrandet — der wird
    // nämlich vom Überfahren hervorgehoben, und zwar genau so wie ein ausgewählter.
    // Man wüsste also bis zum Schluss nicht, was man gerade einfängt.
    const uebernehmen = (treffer) => {
      setAuswahl(alt =>
        (alt.length === treffer.length && alt.every((k, i) => k === treffer[i])) ? alt : treffer);
      onBlockSelect?.(treffer.length === 1 ? treffer[0] : null);
    };

    const onMove = (mv) => {
      if (!gezogen &&
          Math.abs(mv.clientX - sx) < RAHMEN_SCHWELLE &&
          Math.abs(mv.clientY - sy) < RAHMEN_SCHWELLE) return;
      gezogen = true;
      setRahmen({
        links: Math.min(sx, mv.clientX), oben:  Math.min(sy, mv.clientY),
        breite: Math.abs(mv.clientX - sx), hoehe: Math.abs(mv.clientY - sy),
      });
      uebernehmen(trefferIm(mv.clientX, mv.clientY));
    };

    const onUp = (mu) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      setRahmen(null);
      if (!gezogen) return;
      rahmenGezogenRef.current = true;
      uebernehmen(trefferIm(mu.clientX, mu.clientY));
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  };

  // Ein Zug an einem Block der Gruppe verschiebt alle um denselben Betrag — in EINEM
  // Patch, sonst gäbe es pro Block einen eigenen Rückgängig-Schritt.
  // ── Frei angelegte Textfelder ────────────────────────────────
  // Sie brauchen kein eigenes Fach im Layout: Position, Aussehen und Inhalt liegen in
  // denselben drei Töpfen wie bei den festen Blöcken (`offsets`, `block_styles`,
  // `block_html`). Gemerkt wird nur, welche Kennungen frei angelegt wurden.
  const eigeneFelder = stelle?.an_layout?.eigene || [];

  // Eine neue Seite entsteht dadurch, dass ein Block auf ihr liegt — die Seitenzahl
  // ergibt sich ja aus den Blöcken. „Seite hinzufügen" ist deshalb nichts anderes als
  // „leeres Textfeld auf das nächste Blatt legen". Ein Fach weniger, das falsch stehen
  // kann.
  const textfeldHinzufuegen = (aufSeite) => {
    const id = `tf${Date.now().toString(36)}`;
    // Jedes weitere Feld auf derselben Seite rückt ein Stück nach unten rechts, statt
    // sich exakt über das vorige zu legen. Vorher starteten alle in derselben Ecke und
    // waren nicht auseinanderzuhalten — man sah nur das oberste. Nach sechs Feldern
    // beginnt die Treppe von vorn, damit sie nicht vom Blatt läuft.
    const schonDa = eigeneFelder.filter(fid => (blockOffsets?.[fid]?.seite ?? 0) === aufSeite).length;
    const stufe = (schonDa % 6) * 26;
    const naechste = {
      ...blockOffsets,
      // Ein neues Feld startet als längliches Rechteck, nicht als flacher Streifen über
      // die ganze Textbreite: gut ein Drittel breit, und mit einer Mindesthöhe, damit
      // man den Kasten überhaupt als Fläche sieht und anfassen kann.
      [id]: {
        seite: aufSeite,
        top: stufe,
        left: stufe,
        width: contentW ? Math.round(contentW * AN_NEUES_FELD_BREITE) : undefined,
        hoehe: AN_NEUES_FELD_HOEHE,
        dragged: true,
      },
    };
    setBlockOffsets(naechste);
    // Die Seitenzahl wird ausdrücklich mitgeschickt. Ohne sie hinge die neue Seite wieder
    // allein an diesem einen Textfeld und verschwände mit ihm.
    handleLayoutPatch({
      offsets: naechste,
      eigene: [...eigeneFelder, id],
      seiten: Math.max(nPages, aufSeite + 1),
    });
    // Legt der Knopf unten eine neue Seite an, arbeitet der User ab jetzt auf ihr.
    setAktiveSeite(aufSeite);
    selectBlock(id);
  };

  // Wie hoch steht der Block gerade auf dem Papier? Nur im Moment des Ausblendens zu
  // haben — danach ist er weg und niemand könnte ihn mehr messen. Der Wert bestimmt die
  // Höhe seiner Spur; ohne ihn müsste die Spur raten.
  const gemesseneHoehe = (key) =>
    document.querySelector(`.an-pagina--bearbeiten [data-block-key="${key}"]`)?.offsetHeight;

  // Eine Seite geht mitsamt dem, was auf ihr liegt — aber nach derselben Regel wie bei
  // der Entf-Taste: Selbst angelegte Textfelder werden wirklich gelöscht, feste Blöcke
  // nur ausgeblendet. Deren Text gehört zum Brief, und ein einzelner Klick darf ihn nicht
  // endgültig vernichten; sie hinterlassen ihre Kapsel auf der Seite davor. Was hinter
  // der gelöschten Seite lag, rückt um ein Blatt auf.
  const seiteLoeschen = (idx) => {
    const naechste = {};
    for (const [k, v] of Object.entries(blockOffsets || {})) {
      const s = v?.seite ?? 0;
      if (s === idx) {
        if (eigeneFelder.includes(k)) continue;
        naechste[k] = { ...v, seite: Math.max(0, idx - 1), versteckt: true, spurHoehe: gemesseneHoehe(k) };
      } else {
        naechste[k] = s > idx ? { ...v, seite: s - 1 } : v;
      }
    }
    setBlockOffsets(naechste);
    handleLayoutPatch({
      offsets: naechste,
      eigene: eigeneFelder.filter(id => (blockOffsets?.[id]?.seite ?? 0) !== idx),
      seiten: Math.max(1, nPages - 1),
    });
    // Die gelöschte Seite kann nicht die aktive bleiben — es gibt sie nicht mehr.
    setAktiveSeite(s => Math.max(0, Math.min(s, nPages - 2)));
    selectBlock(null);
  };

  // Ein neues Feld über die Werkzeugleiste landet auf der Seite, auf der gerade
  // gearbeitet wird — sonst müsste man es quer über den Brief schleppen.
  // Die Seite, auf der gerade gearbeitet wird. Ist ein Block ausgewählt, gilt dessen
  // Seite — sonst die zuletzt angetippte. Vorher wurde ausschließlich der ausgewählte
  // Block befragt; ohne Auswahl fiel die Antwort stumm auf Seite 1 zurück, und jedes
  // neue Textfeld landete oben auf dem ersten Blatt, egal wo man gerade war.
  const seiteDesAusgewaehlten = blockOffsets?.[selectedBlock]?.seite ?? aktiveSeite;

  const handleEigenesFeldChange = (id, plain, htmlPayload) => {
    handleLayoutPatch({
      block_html: { [id]: { text: plain, html: htmlPayload?.html || null } },
      folge: `feld:${id}`,
    });
  };

  const handleGruppeVerschieben = (keys, delta, folge) => {
    const next = { ...blockOffsets };
    for (const k of keys) {
      const pos = next[k];
      if (!pos || pos.left == null || pos.top == null) continue;
      // Jeder Block der Gruppe rechnet seinen Seitenwechsel für sich aus — sie können
      // auf verschiedenen Blättern liegen, und dann führt ein gemeinsamer Weg zu
      // verschiedenen Zielseiten.
      const lage = seitenMass
        ? seiteNachVerschieben({ seite: pos.seite ?? 0, top: pos.top }, delta.y, seitenMass)
        : { seite: pos.seite ?? 0, top: pos.top + delta.y };
      next[k] = {
        ...pos,
        left: pos.left + delta.x,
        seite: Math.min(lage.seite, nPages - 1),
        top: lage.seite <= nPages - 1 ? lage.top : pos.top + delta.y,
        dragged: true,
      };
    }
    setBlockOffsets(next);
    handleLayoutPatch({ offsets: next, ...(folge ? { folge } : {}) });
  };

  // Beim Überfahren der Schriftliste sieht der Brief die Schrift schon, gespeichert
  // wird sie erst beim Klicken.
  const blockStylesMitVorschau = (fontVorschau != null && selectedBlock)
    ? { ...basisBlockStyles, [selectedBlock]: { ...(basisBlockStyles[selectedBlock] || {}), fontFamily: fontVorschau || undefined } }
    : basisBlockStyles;

  const layoutProps = {
    rohtext: stelle.an_rohtext,
    nutzer: profil,
    stelle,
    template,
    onEditToggle:      inDragMode ? undefined : handleEditToggle,
    onInlineChange: inDragMode ? onInlineChange : undefined,
    onAbsenderToggle:  inDragMode ? undefined : onAbsenderToggle,
    onEmpfaengerToggle: inDragMode ? undefined : onEmpfaengerToggle,
    onDatumToggle:     inDragMode ? undefined : onDatumToggle,
    onAbsenderInlineChange:   inDragMode ? onAbsenderInlineChange : undefined,
    onEmpfaengerInlineChange: inDragMode ? onEmpfaengerInlineChange : undefined,
    onDatumInlineChange:      inDragMode ? onDatumInlineChange : undefined,
    activeEditKey,
    onActiveEditKeyChange: setActiveEditKey,
    blockOffsets,
    onOffsetChange:      inDragMode ? handleOffsetChange : undefined,
    selectedBlock:       inDragMode ? selectedBlock : undefined,
    onSelectBlock:       inDragMode ? selectBlock : undefined,
    auswahl:             inDragMode ? auswahl : undefined,
    onGruppeVerschieben: inDragMode ? handleGruppeVerschieben : undefined,
    blatt,
    sicher:              inDragMode ? sicher : undefined,
    onRandVerletzt:      inDragMode ? meldeRandVerletzung : undefined,
    seitenMass:          inDragMode ? seitenMass : undefined,
    letzteSeite:         nPages - 1,
    onZug:               inDragMode ? meldeZug : undefined,
    onStylePatch:        inDragMode ? handleStylePatch : undefined,
    onKiEdit:            handleEditToggle ? (blockKey) => {
      const { betreff, betreffRohzeile, paragraphen } = parseAnschreiben(stelle.an_rohtext);
      if (blockKey === 'betreff' && betreff) {
        handleEditToggle({ text: betreff, rohzeile: betreffRohzeile, label: 'Betreff' });
      } else if (blockKey === 'text' && paragraphen.length > 0) {
        const p = paragraphen[0];
        handleEditToggle({ text: p.text, rohzeile: p.rohzeile, label: 'Absatz 1' });
      }
    } : undefined,
    blockStyles:         blockStylesMitVorschau,
    blockHtml:           stelle?.an_layout?.block_html || {},
    eigene:              eigeneFelder,
    onEigenesFeldChange: inDragMode ? handleEigenesFeldChange : undefined,
  };

  return (
    <div className="an-vorschau-wrapper">
      {/* Ruler: measures exact pixel height of 257mm at current browser DPI */}
      <div
        ref={rulerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: '-999vw',
          height: '257mm',
          width: '1px',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      />
      {/* Hidden measurement container — position:fixed keeps it off-screen but fully laid out */}
      <div
        className={`an-pagina-${template}`}
        ref={measureRef}
        style={{
          position: 'fixed',
          top: 0,
          left: '-999vw',
          width: '165mm',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <AnschreibenLayout
          {...layoutProps}
          onEditToggle={undefined}
          onAbsenderToggle={undefined}
          onEmpfaengerToggle={undefined}
          activeEditKey={undefined}
          onActiveEditKeyChange={undefined}
          onOffsetChange={undefined}
          imFluss
        />
      </div>

      <div className="an-vorschau-label">
        {(onUndo || onRedo) && (
          <div className="cv-history-btns">
            <button className="cv-history-btn" onClick={onUndo} disabled={!canUndo} title="Rückgängig">
              <UndoIcon />
              {undoCount > 0 && <span className="cv-history-count">{undoCount}</span>}
            </button>
            <button className="cv-history-btn" onClick={onRedo} disabled={!canRedo} title="Wiederherstellen">
              <RedoIcon />
              {redoCount > 0 && <span className="cv-history-count">{redoCount}</span>}
            </button>
          </div>
        )}
        {tabsElement || <span>Anschreiben Vorschau</span>}
      </div>
      {inDragMode && (
        <AnFixedToolbar
          selectedBlock={selectedBlock}
          blockStyles={basisBlockStyles}
          fmtState={fmtState}
          onStylePatch={handleStylePatch}
          onFontVorschau={setFontVorschau}
          stempelAktiv={!!stempel}
          onStempel={handleStempel}
          onTextfeld={() => textfeldHinzufuegen(seiteDesAusgewaehlten)}
        />
      )}
      <div
        ref={aussenRef}
        className={`an-paginas-outer${rahmen ? ' an-zieht-rahmen' : ''}`}
        onClick={() => {
          // Nach einem aufgezogenen Rahmen folgt immer noch ein Klick. Ohne diese
          // Sperre löschte er die eben getroffene Auswahl sofort wieder.
          if (rahmenGezogenRef.current) { rahmenGezogenRef.current = false; return; }
          selectBlock(null);
        }}
      >
        {rahmen && (
          <div
            className="an-auswahlrahmen"
            style={{
              left: rahmen.links + 'px', top: rahmen.oben + 'px',
              width: rahmen.breite + 'px', height: rahmen.hoehe + 'px',
            }}
          />
        )}
        {leftPanel && (
          <div className="an-left-panel-slot">
            {leftPanel}
          </div>
        )}
      <div className="an-paginas-container" ref={ref}>
        {bearbeitenButton}
        {Array.from({ length: nPages }, (_, pageIdx) => (
          <div
            key={pageIdx}
            className={[
              'an-pagina', `an-pagina-${template}`,
              inDragMode && 'an-pagina--bearbeiten',
              inDragMode && randVerletzt && 'an-pagina--randverletzt',
              // Die Seite, von der gerade gezogen wird, muss über den folgenden Blättern
              // liegen — sonst verschwindet der Block beim Ziehen nach unten hinter dem
              // nächsten weißen Blatt.
              zug?.von === pageIdx && 'an-pagina--zieht',
              // Und die Zielseite hebt sich hervor, damit man sieht, wo man landet.
              zug && zug.ziel === pageIdx && zug.ziel !== zug.von && 'an-pagina--zielseite',
              // Nur bei mehreren Blättern markieren. Bei einer einzigen Seite gäbe es
              // nichts zu unterscheiden, und die Markierung wäre bloßes Rauschen.
              inDragMode && nPages > 1 && seiteDesAusgewaehlten === pageIdx && 'an-pagina--aktiv',
            ].filter(Boolean).join(' ')}
            onPointerDown={inDragMode ? (e) => {
              setAktiveSeite(pageIdx);
              // Nur auf freier Fläche. Über einem Block, einer Spur oder einem Knopf
              // gehört der Druck diesem Element — dort wird gezogen, nicht ausgewählt.
              if (e.button !== 0) return;
              if (e.target.closest('.an-drag-block, .an-versteckt-spur, .an-seite-weg')) return;
              // Ohne das fängt der Browser an, beim Ziehen Wörter zu markieren — man
              // zöge dann zwei Auswahlen gleichzeitig auf, eine sichtbare für Blöcke
              // und eine blaue für Text.
              e.preventDefault();
              rahmenStarten(e, e.currentTarget);
            } : undefined}
          >
            {/* Die Seitenzahl steht am Blatt, nicht in einer Leiste: So sieht man beim
                Blättern sofort, wo man ist — und woher ein neues Textfeld kommt. */}
            {inDragMode && nPages > 1 && (
              <span className="an-seite-nummer">Seite {pageIdx + 1} von {nPages}</span>
            )}
            {/* Die erste Seite bleibt. Ein Anschreiben ohne Blatt gibt es nicht, und ein
                Knopf, der genau das anböte, wäre nur eine Falle. */}
            {inDragMode && pageIdx > 0 && (
              <button
                type="button"
                className="an-seite-weg"
                title="Seite löschen"
                onClick={(e) => { e.stopPropagation(); seiteLoeschen(pageIdx); }}
              >
                <SeiteWegIcon />
              </button>
            )}
            {/* Inner div: height constrains flow content; overflow visible lets drag blocks reach page margins */}
            <div style={{ overflow: 'visible', height: '257mm' }}>
              {/* Kein Verschieben mehr: Jede Seite zeichnet ihre eigenen Blöcke, und
                  deren Koordinaten zählen von der Oberkante genau dieser Seite. */}
              {/* Die Höhe wird durchgereicht, damit `.anschreiben` darunter wirklich so
                  hoch ist wie der Textbereich. Ohne das bricht die Kette hier ab und die
                  senkrechte Hilfslinie hätte nichts, woran sie sich aufspannen kann. */}
              <div ref={pageIdx === 0 ? ersteSeiteRef : undefined} style={{ height: '100%' }}>
                <AnschreibenLayout {...layoutProps} seite={pageIdx} />
              </div>
            </div>
          </div>
        ))}
        {/* Unter dem letzten Blatt: eine neue Seite anlegen. Sie kommt gleich mit einem
            leeren Textfeld — sonst wäre sie nicht nur leer, sondern auch nicht
            beschreibbar, und man müsste zwei Handgriffe machen statt einem. */}
        {inDragMode && (
          <div className="an-seite-neu">
            {/* Der Hinweis steht unmittelbar über der Abhilfe: Wer liest, dass etwas
                nicht mehr aufs Blatt passt, hat den Knopf für eine neue Seite direkt
                darunter. */}
            {/* Der Name ist anklickbar und führt zum Block hin. Vorher stand hier nur
                Text — und wer einen Block sucht, der unterhalb des Blattes endet, sucht
                naturgemäß etwas, das er nicht sieht. Eine Beschwerde ohne Weg zur
                Ursache liest sich wie ein Fehler des Programms, nicht wie ein Hinweis. */}
            {ueberlaufKeys.length > 0 && (
              <p className="an-ueberlauf-hinweis">
                {ueberlaufKeys.map((k, i) => (
                  <span key={k}>
                    {i > 0 && ', '}
                    <button
                      type="button"
                      className="an-ueberlauf-ziel"
                      onClick={(e) => { e.stopPropagation(); zeigeBlock(k); }}
                      title="Block anzeigen"
                    >
                      „{AN_BLOCK_NAME[k] || 'Textfeld'}"
                    </button>
                  </span>
                ))}
                {' '}endet unterhalb des Blattes. Gedruckt wird nur, was auf dem Papier steht.
              </p>
            )}
            {/* Ab der Höchstzahl verschwindet der Knopf ganz statt nur grau zu werden:
                Ein Knopf, der nichts tut, ist eine Einladung ins Leere. */}
            {nPages < AN_MAX_SEITEN && (
              <button
                type="button"
                className="an-seite-neu-btn"
                onClick={(e) => { e.stopPropagation(); textfeldHinzufuegen(nPages); }}
                title="Seite hinzufügen"
              >
                <SeiteNeuIcon />
                <span>Seite hinzufügen</span>
              </button>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
});

export default AnschreibenVorschau;
